import React, { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useSearchParams, useParams, useLocation } from "react-router-dom";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { apiUrl } from "../../api/config";
import SearchableSelect from "../../components/common/SearchableSelect";
import Swal from "sweetalert2";
import PDFViewer from "../../components/common/PDFViewer";
import {
  FaClipboardList,
  FaIdCard,
  FaListUl,
  FaStopwatch,
  FaPrint,
  FaFileExcel,
  FaFilePdf,
  FaAward,
  FaCheck,
  FaTimes,
  FaCog,
  FaTrophy,
  FaProjectDiagram,
  FaCalendarAlt,
  FaMapMarkedAlt,
  FaRedo,
  FaTrash,
} from "react-icons/fa";

const toInt = (value) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? 0 : n;
};

const normalizeClassificationToken = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");

const requiresGuideRunnerForClassification = (classification) => {
  const token = normalizeClassificationToken(classification);
  if (!token) return false;
  if (/^(B|B1|B2|B3)$/.test(token)) return true;
  if (/^(T1[123]|F1[123]|S1[123]|SB1[123]|SM1[123]|PTVI|VI|ARVI)$/.test(token)) return true;
  if (token.includes("BLIND") || token.includes("VISUAL")) return true;
  return false;
};

const HUB_TABS = [
  { id: "checkin", label: "Live Matches & Check-in", icon: <FaClipboardList /> },
  { id: "engine", label: "Game Engine Setup", icon: <FaCog /> },
  { id: "schedule", label: "Event Schedule", icon: <FaCalendarAlt /> },
  { id: "results", label: "Result Entry Desk", icon: <FaStopwatch /> },
  { id: "leaderboard", label: "Medals & Leaderboard", icon: <FaTrophy /> },
  { id: "certificates", label: "Certificates Desk", icon: <FaAward /> },
  { id: "districtMedals", label: "District Medals", icon: <FaMapMarkedAlt /> },
];

const isTriathlonGame = (gameType, gameName, sportName, eventTitle) => {
  const g = String(gameName || "").toLowerCase();
  const s = String(sportName || "").toLowerCase();
  const t = String(eventTitle || "").toLowerCase();
  const ty = String(gameType || "").toLowerCase();
  return (
    g.includes("triathlon") ||
    s.includes("triathlon") ||
    t.includes("triathlon") ||
    ty.includes("triathlon")
  );
};

const timeStringToMs = (str) => {
  if (!str) return 0;
  const parts = String(str).trim().split(":");
  if (parts.length === 3) {
    const mm = parseInt(parts[0], 10) || 0;
    const ss = parseInt(parts[1], 10) || 0;
    const ms = parseInt(parts[2].padEnd(3, "0").slice(0, 3), 10) || 0;
    return mm * 60000 + ss * 1000 + ms;
  } else if (parts.length === 2) {
    const mm = parseInt(parts[0], 10) || 0;
    const ss = parseInt(parts[1], 10) || 0;
    return mm * 60000 + ss * 1000;
  }
  return 0;
};

const calculateTriathlonTotalTime = (swim, t1, cycle, t2, run) => {
  const msSwim = timeStringToMs(swim);
  const msT1 = timeStringToMs(t1);
  const msCycle = timeStringToMs(cycle);
  const msT2 = timeStringToMs(t2);
  const msRun = timeStringToMs(run);
  const totalMs = msSwim + msT1 + msCycle + msT2 + msRun;
  if (totalMs === 0) return "";
  const mm = Math.floor(totalMs / 60000);
  const ss = Math.floor((totalMs % 60000) / 1000);
  const mmm = Math.floor(totalMs % 1000);
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}:${String(mmm).padStart(3, "0")}`;
};

const inferEntryMode = (gameType, gameName, sportName, eventTitle) => {
  const type = String(gameType || "").toUpperCase();
  const g = String(gameName || "").toLowerCase();
  const s = String(sportName || "").toLowerCase();
  const t = String(eventTitle || "").toLowerCase();
  if (isTriathlonGame(gameType, gameName, sportName, eventTitle)) {
    return "triathlon";
  }
  if (type === "TIMED" || type === "TRACK" || s.includes("swim") || s.includes("athletic") || /(track|timed|race|run|marathon|sprint|freestyle|backstroke|breaststroke|butterfly|relay)/.test(`${g} ${s} ${t}`)) {
    return "time";
  }
  if (["POINTS", "DISTANCE", "DISTANCE_HEIGHT", "FIELD"].includes(type) || /(throw|put|discus|javelin|club|jump|shotput)/.test(g)) {
    return "distance";
  }
  if (type === "MAX_WEIGHT" || /(powerlifting|weightlifting|bench press)/.test(`${g} ${s}`)) {
    return "weight";
  }
  if (["BRACKET", "KNOCKOUT", "LEAGUE", "TEAM", "TEAM_SPORT", "MATCH", "ROUND"].includes(type) || /(round|set|match|knockout|league|team|bracket)/.test(g)) {
    return "bracket";
  }
  return "points";
};

const formatTimeMask = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 7);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 7)}`;
};

const resolveGameTerms = (gameName, sportName) => {
  const gName = String(gameName || "").toLowerCase();
  const sName = String(sportName || "").toLowerCase();

  if (
    gName.includes("throw") ||
    gName.includes("put") ||
    gName.includes("discus") ||
    gName.includes("javelin") ||
    gName.includes("club") ||
    gName.includes("jump") ||
    gName.includes("shotput") ||
    gName.includes("shot-put")
  ) {
    return { groupLabel: "Group", positionLabel: "Order", shortGroupLabel: "Group", shortPositionLabel: "Order" };
  }

  if (
    sName.includes("badminton") ||
    sName.includes("tennis") ||
    sName.includes("volleyball") ||
    sName.includes("basketball") ||
    sName.includes("goalball") ||
    gName.includes("singles") ||
    gName.includes("doubles")
  ) {
    const position = sName.includes("table tennis") || gName.includes("table tennis") ? "Table" : "Court";
    return { groupLabel: "Round", positionLabel: position, shortGroupLabel: "Round", shortPositionLabel: position };
  }

  if (
    sName.includes("archery") ||
    sName.includes("shooting") ||
    gName.includes("archery") ||
    gName.includes("shooting")
  ) {
    return { groupLabel: "Detail", positionLabel: "Target", shortGroupLabel: "Detail", shortPositionLabel: "Target" };
  }

  if (sName.includes("powerlifting") || gName.includes("powerlifting")) {
    return { groupLabel: "Flight", positionLabel: "Lot No.", shortGroupLabel: "Flight", shortPositionLabel: "Lot No." };
  }

  return { groupLabel: "Heat", positionLabel: "S.No", shortGroupLabel: "Heat No", shortPositionLabel: "S.No" };
};

export default function GamesHub() {
  const location = useLocation();
  const { event_id: routeEventId } = useParams();
  const [searchParams] = useSearchParams();

  // Determine Tab based on Pathname
  const defaultTab = useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes("game-day")) return "checkin";
    if (path.includes("game-engine")) return "engine";
    if (path.includes("results")) return "results";
    if (path.includes("certificates")) return "certificates";
    if (path.includes("medal-tally") || path.includes("leaderboard")) return "leaderboard";
    return "checkin";
  }, [location.pathname]);

  // Core Scope State
  const [date, setDate] = useState(searchParams.get("date") || new Date().toISOString().slice(0, 10));
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(routeEventId || searchParams.get("event_id") || "");
  const [hubGender, setHubGender] = useState("all");
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Tab 1: Check-in State
  const [checkinData, setCheckinData] = useState({
    summary: {},
    participants: [],
    game_breakdown: [],
    classification_breakdown: [],
  });
  const [checkinFilters, setCheckinFilters] = useState({
    gender: "all",
    status: "all",
    q: "",
  });
  const [previewUrl, setPreviewUrl] = useState("");

  // Tab 2: Game Engine Setup State
  const [combineCategories, setCombineCategories] = useState(false);
  const [mergeClassificationIds, setMergeClassificationIds] = useState([]);
  const [ageCategory, setAgeCategory] = useState("all");
  const componentType = "lanes";
  const [componentCount, setComponentCount] = useState(8);
  const [candidates, setCandidates] = useState([]);
  const [manualByReg, setManualByReg] = useState({});
  const [classificationLockInfo, setClassificationLockInfo] = useState({ isLocked: false, unclassifiedCount: 0, totalPlayers: 0 });
  const [adminForceUnlock, setAdminForceUnlock] = useState(false);

  const loadClassificationCompletion = async () => {
    if (!eventId) return;
    try {
      const res = await API.getClassificationCompletionStatus(eventId);
      const data = res?.data || {};
      setClassificationLockInfo({
        isLocked: !data.is_completed && Number(data.unclassified_count || 0) > 0,
        unclassifiedCount: Number(data.unclassified_count || 0),
        totalPlayers: Number(data.total_players || 0),
      });
    } catch (e) {
      console.error("Failed to check classification completion status:", e);
    }
  };

  const [generatedScoreSheets, setGeneratedScoreSheets] = useState([]);

  const loadGeneratedScoreSheets = async () => {
    if (!eventId) {
      setGeneratedScoreSheets([]);
      return;
    }
    try {
      const res = await API.get("/admin/game-engine/generated-score-sheets", {
        params: { event_id: eventId },
      });
      const data = res?.data || res || [];
      setGeneratedScoreSheets(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load generated score sheets:", e);
      setGeneratedScoreSheets([]);
    }
  };
  // Unused variables isEditingHeats, hasAssignedHeats, and heatsGrouped removed to fix warnings

  // Scheduling State
  const [schedules, setSchedules] = useState([]);
  const [schedEventNumbers, setSchedEventNumbers] = useState({});
  const [schedTimes, setSchedTimes] = useState({});

  // Classification Counts and Dropdown State
  const [classCounts, setClassCounts] = useState({});
  const [selectedClassDropdown, setSelectedClassDropdown] = useState("all");

  // Tab 3: Results Entry State
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("");
  const [classifications, setClassifications] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [players, setPlayers] = useState([]);
  const [entryByRow, setEntryByRow] = useState({});
  const [entryMode, setEntryMode] = useState("auto");
  const [mixClassifications, setMixClassifications] = useState(false);
  const [allPlayersForStatus, setAllPlayersForStatus] = useState([]);

  // Live Settings
  const [liveSettingsVisible, setLiveSettingsVisible] = useState(false);
  const [liveEnabled, setLiveEnabled] = useState(true);
  const [pauseMessage, setPauseMessage] = useState("");
  const [liveSettingsSaving, setLiveSettingsSaving] = useState(false);

  // Bracket/Matches Entry State
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [roundName, setRoundName] = useState("Round 1");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [liveMatchId, setLiveMatchId] = useState("");
  const [liveScore, setLiveScore] = useState({ left: 0, right: 0 });
  const [liveStep, setLiveStep] = useState(1);
  const [winnerByMatch, setWinnerByMatch] = useState({});
  const [scoreByMatch, setScoreByMatch] = useState({});

  // Tab 5: Issued Certificate Suite
  const [issuedCertificates, setIssuedCertificates] = useState([]);
  const [districtMedals, setDistrictMedals] = useState([]);

  // Computed Context
  const selectedEvent = useMemo(
    () => events.find((e) => String(e.id) === String(eventId)) || null,
    [events, eventId]
  );

  const selectedGame = useMemo(
    () => games.find((g) => String(g.id) === String(gameId)) || null,
    [games, gameId]
  );

  const effectiveEntryMode = useMemo(() => {
    if (entryMode !== "auto") return entryMode;
    if (selectedEvent?.title && isTriathlonGame(selectedGame?.game_type, selectedGame?.game_name, selectedEvent?.sport_name, selectedEvent?.title)) {
      return "triathlon";
    }
    if (!selectedGame) return "points";
    return inferEntryMode(selectedGame.game_type, selectedGame.game_name, selectedEvent?.sport_name || "", selectedEvent?.title || "");
  }, [selectedGame, selectedEvent, entryMode]);

  const terms = useMemo(
    () => resolveGameTerms(selectedGame?.game_name, selectedEvent?.sport_name || selectedEvent?.sport),
    [selectedGame, selectedEvent]
  );

  const isAthleticsEvent = useMemo(() => {
    const sport = String(selectedEvent?.sport_name || selectedEvent?.sport || "").toLowerCase();
    const title = String(selectedEvent?.title || "").toLowerCase();
    const sportId = Number(selectedEvent?.sport_id || 0);
    return sportId === 2 || sport.includes("athlet") || title.includes("athlet");
  }, [selectedEvent]);

  const isTeamMode = useMemo(() => {
    if (!selectedGame) return false;
    const type = String(selectedGame.game_type || "").toUpperCase();
    const name = String(selectedGame.game_name || "").toLowerCase();
    const narration = String(selectedGame.game_narration || "").toLowerCase();
    const isTeamBracket = type === "BRACKET" && /(doubles|pairs|mixed|team)/.test(`${name} ${narration}`);
    const isRelay = /(relay|4x)/.test(`${name} ${narration}`);
    return type === "TEAM_SPORT" || isTeamBracket || isRelay;
  }, [selectedGame]);

  const engineMode = useMemo(() => {
    if (!eventId || !gameId) return "unselected";
    if (gameId === "all") return "hybrid";
    const type = String(selectedGame?.game_type || "").toUpperCase();
    const name = String(selectedGame?.game_name || "").toLowerCase();
    const narration = String(selectedGame?.game_narration || "").toLowerCase();
    const relayLike = /(relay|4x)/.test(`${name} ${narration}`);
    const isFixture = relayLike || ["BRACKET", "KNOCKOUT", "LEAGUE", "TEAM", "MATCH", "ROUND"].some((t) => type.includes(t));
    const isHeat = type.includes("TIMED") || type.includes("TRACK") || ["POINTS", "DISTANCE", "DISTANCE_HEIGHT", "MAX_WEIGHT"].some((t) => type.includes(t));
    if (isHeat && isFixture) return "hybrid";
    if (isFixture) return "fixture";
    if (isHeat) return "heat";
    return "unselected";
  }, [eventId, gameId, selectedGame]);

  // Load Events on target date
  const loadEvents = async (targetDate) => {
    setLoading(true);
    try {
      const res = await API.fetchEventsByDate(targetDate);
      const list = res?.data || res || [];
      const arr = Array.isArray(list) ? list : [];
      setEvents(arr);
      if (arr.length > 0) {
        if (!routeEventId && !arr.some((e) => String(e.id) === String(eventId))) {
          setEventId(String(arr[0].id));
        }
      } else {
        setEventId("");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load events for date.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Load check-in dashboard data
  const loadCheckinData = async (overrideGender) => {
    if (!eventId) return;
    setLoading(true);
    try {
      const activeGender = overrideGender !== undefined ? overrideGender : hubGender;
      const res = await API.get("/admin/game-day/v2/dashboard", {
        params: {
          event_id: eventId,
          ...checkinFilters,
          gender: activeGender === "all" ? undefined : activeGender,
        },
      });
      const data = res?.data || res?.data?.data || res || {};
      setCheckinData({
        summary: data?.summary || {},
        participants: Array.isArray(data?.participants) ? data.participants : [],
        game_breakdown: Array.isArray(data?.game_breakdown) ? data.game_breakdown : [],
        classification_breakdown: Array.isArray(data?.classification_breakdown) ? data.classification_breakdown : [],
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Update check-in registration status
  const handleUpdateStatus = async (regId, status) => {
    try {
      await API.updateRegistrationStatus(regId, status);
      Swal.fire({
        title: "Updated",
        text: `Competitor status updated to ${status}.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
      setCheckinData((prev) => ({
        ...prev,
        participants: (prev.participants || []).map((p) =>
          String(p.registration_id) === String(regId) ? { ...p, status } : p
        ),
      }));
      loadCheckinData();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to update status.", "error");
    }
  };

  // Load context for results
  const loadResultsContext = async () => {
    if (!eventId) {
      setGames([]);
      setClassifications([]);
      setPlayers([]);
      setEntryByRow({});
      return;
    }
    try {
      const [ctxRes, clsRes] = await Promise.all([
        API.fetchResultEventContext(eventId),
        selectedEvent?.sport_id
          ? API.get(`/admin/classifications/sports/${selectedEvent.sport_id}/classifications`)
          : Promise.resolve([]),
      ]);
      const ctx = ctxRes?.data || ctxRes || {};
      const gameList = Array.isArray(ctx?.games) ? ctx.games : [];
      setGames(gameList);
      setClassifications(Array.isArray(clsRes?.data) ? clsRes.data : Array.isArray(clsRes) ? clsRes : []);

      if (gameList.length > 0) {
        setGameId(String(gameList[0].id));
      } else {
        setGameId("");
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Load players context for results entry
  const loadPlayersForResults = async () => {
    if (!eventId || !gameId) return;
    setBusy(true);
    try {
      const params = {
        game_id: Number(gameId),
        gender: hubGender === "all" ? undefined : hubGender,
        classification_ids: selectedClassIds.length ? selectedClassIds.map(Number) : undefined,
      };
      const res = await API.fetchEventPlayers(eventId, params);
      const rows = res?.data || res || [];
      const list = Array.isArray(rows) ? rows : [];
      setPlayers(list);

      const nextEntries = {};
      list.forEach((r) => {
        const key = `${r.registration_id}_${r.game_id || 0}`;
        nextEntries[key] = {
          result_time: r.result_time || "",
          result_position: r.result_position || "",
          points_score: r.points_score || "",
          attempts: Array.isArray(r.attempts) ? r.attempts.map(String) : ["", "", ""],
        };
      });
      setEntryByRow(nextEntries);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load players for game.", "error");
    } finally {
      setBusy(false);
    }
  };

  const loadAllPlayersForStatus = async () => {
    if (!eventId || !gameId) {
      setAllPlayersForStatus([]);
      return;
    }
    try {
      const res = await API.fetchEventPlayers(eventId, { game_id: Number(gameId) });
      const rows = res?.data || res || [];
      setAllPlayersForStatus(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error(err);
    }
  };

  // Load team and matchups
  const loadTeamsAndMatches = async () => {
    if (!eventId || !gameId) return;
    try {
      const [teamsRes, matchesRes] = await Promise.all([
        API.listTeams(eventId, { game_id: gameId }),
        API.listMatches(eventId, { game_id: gameId }),
      ]);
      setTeams(Array.isArray(teamsRes?.data) ? teamsRes.data : Array.isArray(teamsRes) ? teamsRes : []);
      setMatches(Array.isArray(matchesRes?.data) ? matchesRes.data : Array.isArray(matchesRes) ? matchesRes : []);
    } catch (err) {
      console.error(err);
    }
  };

  // Load Live results settings
  const loadLiveSettings = async () => {
    try {
      const res = await API.fetchLiveResultSettings();
      setLiveEnabled(res?.is_enabled !== false);
      setPauseMessage(String(res?.pause_message || ""));
      setLiveSettingsVisible(true);
    } catch (err) {
      setLiveSettingsVisible(false);
    }
  };

  // Save live results settings
  const handleSaveLiveSettings = async (nextEnabled) => {
    setLiveSettingsSaving(true);
    try {
      const res = await API.updateLiveResultSettings({
        is_enabled: nextEnabled,
        pause_message: pauseMessage,
      });
      setLiveEnabled(res?.data?.is_enabled !== false);
      setPauseMessage(String(res?.data?.pause_message || pauseMessage || ""));
      Swal.fire("Success", nextEnabled ? "Live results enabled." : "Live results paused.", "success");
    } catch (err) {
      Swal.fire("Error", "Failed to update settings.", "error");
    } finally {
      setLiveSettingsSaving(false);
    }
  };

  // Create Team
  const handleCreateTeam = async () => {
    if (!newTeamName || !gameId) return;
    try {
      await API.createTeam(eventId, {
        team_name: newTeamName,
        game_id: Number(gameId),
      });
      setNewTeamName("");
      Swal.fire("Success", "Team created.", "success");
      loadTeamsAndMatches();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to create team.", "error");
    }
  };

  // Add team member
  const handleAddTeamMember = async (teamId, regId) => {
    try {
      await API.addTeamMember(teamId, { registration_id: regId });
      loadTeamsAndMatches();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to add member.", "error");
    }
  };

  // Create Match
  const handleCreateMatch = async () => {
    if (!gameId || !p1 || !p2) {
      Swal.fire("Error", "Select game and both competitors.", "error");
      return;
    }
    try {
      await API.createMatch(eventId, {
        game_id: Number(gameId),
        round_name: roundName,
        player1_reg_id: isTeamMode ? null : Number(p1),
        player2_reg_id: isTeamMode ? null : Number(p2),
        team1_id: isTeamMode ? Number(p1) : null,
        team2_id: isTeamMode ? Number(p2) : null,
        match_order: matches.length + 1,
      });
      setP1("");
      setP2("");
      Swal.fire("Success", "Match created.", "success");
      loadTeamsAndMatches();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to create match.", "error");
    }
  };

  // Save Match Result
  const handleSaveMatchResult = async (match, winnerId, score) => {
    try {
      await API.saveMatchResult(match.id, {
        winner_reg_id: isTeamMode ? null : Number(winnerId || 0),
        winner_team_id: isTeamMode ? Number(winnerId || 0) : null,
        score,
      });
      Swal.fire("Saved", "Match result saved.", "success");
      loadTeamsAndMatches();
    } catch (err) {
      Swal.fire("Error", err?.message || "Save failed.", "error");
    }
  };

  // Dynamic attempts inline best calculator
  const getBestAttempt = (attempts = []) => {
    let best = null;
    attempts.forEach((item) => {
      const n = Number(String(item ?? "").trim());
      if (!Number.isFinite(n)) return;
      if (best === null || n > best) best = n;
    });
    return best !== null ? best : "";
  };

  const autoCalculateRanks = (currentEntries) => {
    const updated = { ...currentEntries };

    // Sort players who have at least one attempt score by best score descending
    const activeScores = players.map((p) => {
      const key = `${p.registration_id}_${p.game_id || 0}`;
      const entry = updated[key] || { attempts: ["", "", ""] };
      const best = getBestAttempt(entry.attempts);
      return {
        key,
        best: best ? parseFloat(best) : 0,
      };
    });

    const withScores = activeScores.filter((x) => x.best > 0);
    withScores.sort((a, b) => b.best - a.best);

    let rank = 1;
    withScores.forEach((item, idx) => {
      if (idx > 0 && item.best < withScores[idx - 1].best) {
        rank = idx + 1;
      }
      if (updated[item.key]) {
        updated[item.key] = {
          ...updated[item.key],
          result_position: String(rank),
        };
      }
    });

    // For any rows that don't have attempts, clear rank
    activeScores.forEach((item) => {
      if (item.best === 0 && updated[item.key]) {
        updated[item.key] = {
          ...updated[item.key],
          result_position: "",
        };
      }
    });

    return updated;
  };

  // Persist manual entries to backend helper
  const persistManualResults = async () => {
    if (!players.length) return;
    if (effectiveEntryMode === "time" || effectiveEntryMode === "triathlon") {
      const results = players.map((r) => {
        const key = `${r.registration_id}_${r.game_id || 0}`;
        const v = entryByRow[key] || {};
        let rawTime = v.result_time || "";
        if (effectiveEntryMode === "triathlon") {
          const calcTotal = calculateTriathlonTotalTime(v.tri_swim, v.tri_t1, v.tri_cycle, v.tri_t2, v.tri_run);
          if (calcTotal) rawTime = calcTotal;
        }
        return {
          registration_id: Number(r.registration_id),
          game_id: Number(r.game_id || gameId),
          result_time: rawTime || null,
          result_position: v.result_position !== "" ? Number(v.result_position) : null,
        };
      });
      await API.saveTimedResults({ results });

      if (effectiveEntryMode === "triathlon") {
        const attemptsPayload = [];
        players.forEach((r) => {
          const key = `${r.registration_id}_${r.game_id || 0}`;
          const v = entryByRow[key] || {};
          const segments = [
            { no: 1, val: v.tri_swim ? `Swim: ${v.tri_swim}` : null },
            { no: 2, val: v.tri_cycle ? `Cycle: ${v.tri_cycle}` : null },
            { no: 3, val: v.tri_run ? `Run: ${v.tri_run}` : null },
          ];
          segments.forEach((seg) => {
            if (seg.val) {
              attemptsPayload.push({
                event_reg_id: Number(r.registration_id),
                registration_id: Number(r.registration_id),
                game_id: Number(r.game_id || gameId),
                selected_game_id: Number(gameId || 0) || null,
                attempt_no: seg.no,
                score: seg.val,
              });
            }
          });
        });
        if (attemptsPayload.length) {
          try { await API.savePointsAttempts({ results: attemptsPayload }); } catch (_e) {}
        }
      }
    } else if (effectiveEntryMode === "distance") {
      const attemptsPayload = [];
      players.forEach((r) => {
        const key = `${r.registration_id}_${r.game_id || 0}`;
        const v = entryByRow[key] || {};
        const attempts = Array.isArray(v.attempts) ? v.attempts : ["", "", ""];
        attempts.forEach((score, idx) => {
          const normalizedScore = String(score ?? "").trim();
          if (normalizedScore !== "") {
            attemptsPayload.push({
              event_reg_id: Number(r.registration_id),
              registration_id: Number(r.registration_id),
              game_id: Number(r.game_id || gameId),
              selected_game_id: Number(gameId || 0) || null,
              attempt_no: idx + 1,
              score: normalizedScore,
            });
          }
        });
      });
      if (attemptsPayload.length) {
        await API.savePointsAttempts({ results: attemptsPayload });
      }

      // Save position/rank entries
      const results = players.map((r) => {
        const key = `${r.registration_id}_${r.game_id || 0}`;
        const v = entryByRow[key] || {};
        return {
          registration_id: Number(r.registration_id),
          game_id: Number(r.game_id || gameId),
          result_position: v.result_position !== "" ? Number(v.result_position) : null,
        };
      }).filter((item) => item.result_position !== null);
      if (results.length) {
        await API.post("/admin/results/generic/save", { results });
      }
    } else {
      const results = players.map((r) => {
        const key = `${r.registration_id}_${r.game_id || 0}`;
        const v = entryByRow[key] || {};
        return {
          registration_id: Number(r.registration_id),
          game_id: Number(r.game_id || gameId),
          points_score: v.points_score !== "" ? Number(v.points_score) : null,
          result_position: v.result_position !== "" ? Number(v.result_position) : null,
        };
      });
      await API.post("/admin/results/generic/save", { results });
    }
  };

  // Render PDF/Excel/Print buttons for results page according to selected options
  const renderExportButtons = (exportType) => {
    if (!eventId || !gameId) return null;

    const queryParts = [];
    if (gameId && gameId !== "all") {
      queryParts.push(`game_id=${gameId}`);
    }
    if (hubGender && hubGender !== "all") {
      queryParts.push(`gender=${hubGender}`);
    }
    if (selectedClassIds.length) {
      queryParts.push(`classification_ids=${selectedClassIds.join(",")}`);
    }
    const queryString = queryParts.length ? `?${queryParts.join("&")}` : "";

    const pdfUrl = apiUrl(`/admin/results/export/${exportType}/${eventId}${queryString}${queryString ? "&" : "?"}format=pdf`);
    const excelUrl = apiUrl(`/admin/results/export/${exportType}/${eventId}${queryString}${queryString ? "&" : "?"}format=excel`);

    return (
      <div className="flex items-center gap-2">
        <a
          href={pdfUrl}
          target="_blank"
          rel="noreferrer"
          className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 font-bold text-xs text-slate-700 flex items-center gap-1.5"
        >
          <FaFilePdf /> Export PDF
        </a>
        <a
          href={excelUrl}
          target="_blank"
          rel="noreferrer"
          className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 font-bold text-xs text-slate-700 flex items-center gap-1.5"
        >
          <FaFileExcel /> Export Excel
        </a>
        <button
          onClick={() => window.print()}
          className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 font-bold text-xs text-slate-700 flex items-center gap-1.5"
        >
          <FaPrint /> Print Sheet
        </button>
      </div>
    );
  };
  // Render PDF/Excel/Print buttons for leaderboard (Tab 4) according to selected options
  const renderLeaderboardExportButtons = () => {
    if (!eventId || !gameId) return null;

    const queryParts = [];
    if (gameId && gameId !== "all") {
      queryParts.push(`game_id=${gameId}`);
    }
    if (hubGender && hubGender !== "all") {
      queryParts.push(`gender=${hubGender}`);
    }
    if (selectedClassIds.length) {
      queryParts.push(`classification_ids=${selectedClassIds.join(",")}`);
    }
    const queryString = queryParts.length ? `?${queryParts.join("&")}` : "";

    const exportType = effectiveEntryMode === "time" ? "timed" : "points";

    // Awarded Only PDF Url
    const awardedPdfUrl = apiUrl(`/admin/results/export/${exportType}/${eventId}${queryString}${queryString ? "&" : "?"}awarded_only=1&format=pdf`);

    // All Positions PDF Url
    const allPdfUrl = apiUrl(`/admin/results/export/${exportType}/${eventId}${queryString}${queryString ? "&" : "?"}format=pdf`);

    // All Positions Excel Url
    const allExcelUrl = apiUrl(`/admin/results/export/${exportType}/${eventId}${queryString}${queryString ? "&" : "?"}format=excel`);

    return (
      <div className="flex items-center gap-2">
        <a
          href={awardedPdfUrl}
          target="_blank"
          rel="noreferrer"
          className="px-3.5 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm"
        >
          <FaFilePdf /> Export Awarded (PDF)
        </a>
        <a
          href={allPdfUrl}
          target="_blank"
          rel="noreferrer"
          className="px-3.5 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 font-bold text-xs text-slate-700 flex items-center gap-1.5"
        >
          <FaFilePdf /> Export All Positions (PDF)
        </a>
        <a
          href={allExcelUrl}
          target="_blank"
          rel="noreferrer"
          className="px-3.5 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 font-bold text-xs text-slate-700 flex items-center gap-1.5"
        >
          <FaFileExcel /> Export All Positions (Excel)
        </a>
      </div>
    );
  };

  // Render a beautiful header when printing the results sheets
  const renderPrintHeader = () => {
    const selectedGameName = games.find(g => String(g.id) === String(gameId))?.game_name || "All";
    const selectedClassesStr = selectedClassIds.length
      ? selectedClassIds.map(cid => {
        const cObj = classifications.find(c => Number(c.id) === Number(cid));
        return cObj ? cObj.classification : cid;
      }).join(", ")
      : "All Classifications";

    return (
      <div className="hidden print:block mb-6 text-center border-b-2 border-indigo-900 pb-4">
        <h1 className="text-xl font-black text-slate-900 tracking-wide">PARASPORTS ASSOCIATION OF ANDHRA PRADESH</h1>
        <h2 className="text-md font-bold text-slate-700 mt-1">{selectedEvent?.title || `Event #${eventId}`}</h2>
        <div className="text-[11px] text-slate-500 mt-1">
          Date: {selectedEvent?.date ? new Date(selectedEvent.date).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "-"} | Venue: {selectedEvent?.location || "-"}
        </div>
        <div className="text-xs font-bold text-indigo-700 mt-2 uppercase">
          Discipline: {selectedGameName} | Gender: {hubGender === "all" ? "All Genders" : hubGender} | Class: {selectedClassesStr}
        </div>
      </div>
    );
  };

  // Compute status based on game, gender, and classification selection
  const getSelectedStatus = () => {
    // Filter the full unfiltered snapshot the same way the results table is filtered,
    // so the badge doesn't depend on the (async) filtered `players` state lagging behind.
    const relevant = allPlayersForStatus.filter((p) => {
      if (hubGender !== "all" && String(p.gender || "").toLowerCase() !== hubGender) return false;
      if (selectedClassIds.length && !selectedClassIds.includes(String(p.classification_id))) return false;
      return true;
    });

    if (!relevant.length) {
      return { text: "No Competitors Registered", badge: "bg-slate-100 text-slate-500 border-slate-200" };
    }
    const withRank = relevant.filter((p) => p.result_position && Number(p.result_position) > 0);
    if (withRank.length === relevant.length) {
      return { text: "Results Completed", badge: "bg-emerald-100 text-emerald-800 border-emerald-250 font-extrabold" };
    }
    if (withRank.length > 0) {
      return { text: "Partially Completed", badge: "bg-blue-100 text-blue-800 border-blue-250 font-extrabold" };
    }
    return { text: "Results Pending", badge: "bg-amber-100 text-amber-800 border-amber-250 font-extrabold" };
  };

  const getClassificationStatus = (classId) => {
    const subset = allPlayersForStatus.filter((p) => {
      if (String(p.classification_id) !== String(classId)) return false;
      if (hubGender !== "all" && String(p.gender || "").toLowerCase() !== hubGender) return false;
      return true;
    });
    if (!subset.length) {
      return { label: "No Registrations", badge: "bg-slate-100 text-slate-500 border-slate-200", dot: "bg-slate-400" };
    }
    const withRank = subset.filter((p) => p.result_position && Number(p.result_position) > 0);
    if (withRank.length === subset.length) {
      return { label: "Completed", badge: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" };
    }
    if (withRank.length > 0) {
      return { label: "Partial", badge: "bg-blue-100 text-blue-800 border-blue-200", dot: "bg-blue-500" };
    }
    return { label: "Pending", badge: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" };
  };

  // Save scoring sheet
  const saveResults = async () => {
    if (!players.length) return;
    setBusy(true);
    try {
      await persistManualResults();
      Swal.fire("Saved", "Scoring sheets updated successfully.", "success");
      loadPlayersForResults();
      loadAllPlayersForStatus();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.message || "Failed to save results.", "error");
    } finally {
      setBusy(false);
    }
  };

  const groupPlayersByEvent = (playerList) => {
    if (!Array.isArray(playerList)) return [];
    const groups = new Map();
    playerList.forEach((p) => {
      const gName = p.game_name || "General";
      const cName = p.classification_name || p.classification || "Open";
      const aCat = p.age_category || "All";
      const gender = p.gender ? String(p.gender).toUpperCase() : "ALL";
      const key = `${gName}||${cName}||${aCat}||${gender}`;
      if (!groups.has(key)) {
        groups.set(key, { gName, cName, aCat, gender, rows: [] });
      }
      groups.get(key).rows.push(p);
    });
    return Array.from(groups.values());
  };

  // Compute final positions & ranks
  const computeRanks = async () => {
    if (!eventId || !gameId) return;
    setBusy(true);
    try {
      await persistManualResults();
      await API.computePositions({
        event_id: Number(eventId),
        game_id: Number(gameId),
        mix_classifications: mixClassifications,
        mix_classification_ids: mixClassifications ? selectedClassIds.map(Number) : [],
      });
      Swal.fire("Computed", "Positions and medals calculated.", "success");
      loadPlayersForResults();
      loadAllPlayersForStatus();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.message || "Calculation failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  // Download printable PDF reports
  const downloadGameDayFile = async (path, fallbackName, format = "pdf", gender = "all", requestedGameId = null) => {
    if (!eventId) return;
    try {
      const res = await API.get(path, {
        params: { gender: gender || "all", format, ...(requestedGameId ? { game_id: requestedGameId } : {}) },
        responseType: "blob",
      });
      const blob = res?.data;
      if (!blob) throw new Error("Empty file response.");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      Swal.fire("Error", "Unable to download report.", "error");
    }
  };

  const downloadAttendanceSheet = async (format = "pdf") => {
    await downloadGameDayFile(
      `/admin/game-day/attendance-sheet/${eventId}`,
      `attendance_sheet_event_${eventId}.${format === "excel" ? "xlsx" : "pdf"}`,
      format,
      hubGender
    );
  };

  const downloadEventList = async (format = "pdf") => {
    await downloadGameDayFile(
      `/admin/game-day/event-list/${eventId}`,
      `event_list_${eventId}.${format === "excel" ? "xlsx" : "pdf"}`,
      format,
      hubGender
    );
  };

  const downloadAthleticsBibCards = async (format = "pdf") => {
    await downloadGameDayFile(
      `/admin/game-day/athletics-bib-cards/${eventId}`,
      `athletics_bib_cards_event_${eventId}.${format === "excel" ? "xlsx" : "pdf"}`,
      format,
      hubGender
    );
  };

  const downloadHeatSchedule = async (format = "pdf", requestedGameId = null) => {
    await downloadGameDayFile(
      `/admin/game-day/heat-schedule/${eventId}`,
      `heat_schedule_event_${eventId}.${format === "excel" ? "xlsx" : "pdf"}`,
      format,
      hubGender,
      requestedGameId
    );
  };

  const downloadTimeSlips = async (format = "pdf") => {
    await downloadGameDayFile(
      `/admin/game-day/heat-schedule/${eventId}`,
      `time_slips_event_${eventId}${gameId ? `_game_${gameId}` : ""}.${format === "excel" ? "xlsx" : "pdf"}`,
      format,
      hubGender,
      gameId || null
    );
  };

  const downloadScoreSheet = async (format = "pdf", requestedGameId = null) => {
    const targetGameId = requestedGameId || gameId;
    if (!eventId || !targetGameId) return;
    const path = `/admin/game-day/score-sheet/${eventId}`;
    const fallbackName = `score_sheet_event_${eventId}_game_${targetGameId}.${format === "excel" ? "xlsx" : "pdf"}`;

    try {
      setBusy(true);
      const res = await API.get(path, {
        params: {
          gender: hubGender,
          format: format === "print" ? "pdf" : format,
          game_id: targetGameId
        },
        responseType: "blob",
      });
      const blob = res?.data;
      if (!blob) throw new Error("Empty file response.");

      if (format === "print") {
        const pdfBlob = new Blob([blob], { type: "application/pdf" });
        const url = window.URL.createObjectURL(pdfBlob);
        setPreviewUrl(url);
      } else {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fallbackName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Unable to download score sheet.", "error");
    } finally {
      setBusy(false);
    }
  };

  const printGameDayFile = async (path) => {
    if (!eventId) return;
    try {
      const res = await API.get(path, {
        params: { gender: hubGender || "all", format: "pdf" },
        responseType: "blob",
      });
      const blob = res?.data;
      if (!blob) throw new Error("Empty file response.");
      const pdfBlob = new Blob([blob], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      setPreviewUrl(url);
    } catch (e) {
      Swal.fire("Error", "Unable to prepare document for printing.", "error");
    }
  };

  const handleClearGeneratedEvent = async (item) => {
    if (!eventId) return;
    const targetGameName = item.game_name || "Selected Game";
    const result = await Swal.fire({
      title: `Clear heats for ${targetGameName}?`,
      text: `This will clear generated heats and reset setup for ${targetGameName} (${item.gender || "All"}, ${item.classification_names?.join(", ") || "All"}).`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Clear Heats",
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setBusy(true);
    try {
      const gId = item.game_id || "all";
      const genders = item.gender && item.gender !== "all" ? [item.gender] : null;

      if (item.mode === "fixture") {
        await API.post("/admin/game-engine/fixtures/clear", {
          event_id: Number(eventId),
          game_id: gId,
          log_id: item.id || null,
        });
      } else {
        await API.post("/admin/game-engine/heats/clear", {
          event_id: Number(eventId),
          game_id: gId,
          genders: genders,
          log_id: item.id || null,
        });
      }

      Swal.fire("Cleared", `Generated heats for ${targetGameName} cleared successfully.`, "success");
      loadCandidates();
      loadClassificationCounts();
      loadGeneratedScoreSheets();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to clear heats for this event.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleClearAllGeneratedEvents = async () => {
    if (!eventId) return;
    const result = await Swal.fire({
      title: "Clear all generated events & heats?",
      text: "This will reset all generated heats, lane assignments, knockout fixtures, and score sheets for all games in this event.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Clear All",
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setBusy(true);
    try {
      await API.post("/admin/game-engine/heats/clear", {
        event_id: Number(eventId),
        game_id: "all",
      });
      await API.post("/admin/game-engine/fixtures/clear", {
        event_id: Number(eventId),
        game_id: "all",
      });
      Swal.fire("Cleared", "All generated heats and fixtures cleared successfully.", "success");
      loadCandidates();
      loadClassificationCounts();
      loadGeneratedScoreSheets();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to clear heats.", "error");
    } finally {
      setBusy(false);
    }
  };

  // Game Engine Setup Actions
  const runRecommendedSetup = async () => {
    if (engineMode === "fixture") {
      await generateFixtures();
    } else {
      await generateHeats();
    }
  };

  const generateHeats = async () => {
    if (!eventId || !gameId) return;
    setBusy(true);
    try {
      const genders = hubGender === "all" ? ["male", "female"] : [hubGender];
      const res = await API.post("/admin/game-engine/heats", {
        event_id: eventId,
        game_id: gameId,
        genders,
        combine_categories: combineCategories,
        age_category: ageCategory,
        component_type: componentType,
        component_count: Number(componentCount) || 1,
        classification_ids: selectedClassIds.length > 0 ? selectedClassIds.map(Number) : null,
      });
      Swal.fire("Success", res?.message || "Heats generated successfully.", "success");
      loadCandidates();
      loadGeneratedScoreSheets();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to generate heats.", "error");
    } finally {
      setBusy(false);
    }
  };

  const generateFixtures = async () => {
    if (!eventId || !gameId) return;
    setBusy(true);
    try {
      const res = await API.post("/admin/game-engine/fixtures", {
        event_id: eventId,
        game_id: gameId,
        gender: "all",
        combine_categories: combineCategories,
        age_category: ageCategory,
        classification_ids: selectedClassIds.length > 0 ? selectedClassIds.map(Number) : null,
      });
      Swal.fire("Success", res?.message || "Fixtures generated successfully.", "success");
      loadGeneratedScoreSheets();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to generate fixtures.", "error");
    } finally {
      setBusy(false);
    }
  };

  const clearHeats = async () => {
    const confirm = await Swal.fire({
      title: "Clear Heats?",
      text: "This will remove all generated heats.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Clear",
    });
    if (!confirm.isConfirmed) return;
    setBusy(true);
    try {
      const genders = hubGender === "all" ? ["male", "female"] : [hubGender];
      await API.clearHeats({
        event_id: Number(eventId),
        game_id: gameId === "all" ? "all" : Number(gameId),
        genders
      });
      Swal.fire("Success", "Heats cleared.", "success");
      await loadCandidates();
      loadGeneratedScoreSheets();
    } catch (err) {
      Swal.fire("Error", "Failed to clear heats.", "error");
    } finally {
      setBusy(false);
    }
  };

  const clearFixtures = async () => {
    const confirm = await Swal.fire({
      title: "Clear Fixtures?",
      text: "This will remove all generated fixtures.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Clear",
    });
    if (!confirm.isConfirmed) return;
    setBusy(true);
    try {
      await API.clearFixtures({ event_id: Number(eventId), game_id: gameId === "all" ? "all" : Number(gameId) });
      Swal.fire("Success", "Fixtures cleared.", "success");
      await loadCandidates();
      loadGeneratedScoreSheets();
    } catch (err) {
      Swal.fire("Error", "Failed to clear fixtures.", "error");
    } finally {
      setBusy(false);
    }
  };

  const resetResultSheetLocks = async () => {
    setBusy(true);
    try {
      await API.post("/admin/game-engine/result-sheet/reset", { event_id: Number(eventId), game_id: gameId === "all" ? "all" : Number(gameId) });
      Swal.fire("Success", "Result locks reset successfully.", "success");
      await loadCandidates();
      loadGeneratedScoreSheets();
    } catch (err) {
      Swal.fire("Error", "Failed to reset result sheet locks.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleGameChange = (newGId) => {
    setGameId(newGId);
    if (eventId && newGId) {
      loadCandidates(newGId);
    } else {
      setCandidates([]);
      setClassCounts({});
    }
  };

  const loadCandidates = async (overrideGameId) => {
    const targetGameId = overrideGameId !== undefined ? overrideGameId : gameId;
    if (!eventId || !targetGameId) return;
    setBusy(true);
    try {
      const genders = hubGender === "all" ? ["male", "female"] : [hubGender];
      const res = await API.get("/admin/game-engine/heat-candidates", {
        params: {
          event_id: eventId,
          game_id: targetGameId,
          combine_categories: combineCategories,
          classification_ids: selectedClassIds,
          age_category: ageCategory,
          genders,
        },
      });
      const rows = res?.data || res || [];
      const list = Array.isArray(rows) ? rows : [];
      setCandidates(list);
      loadClassificationCounts(hubGender, targetGameId);

      const initial = {};
      list.forEach((r) => {
        const key = `${r.registration_id}_${r.game_id || 0}`;
        initial[key] = { heat_number: r.heat_number || "", lane_number: r.lane_number || "" };
      });
      setManualByReg(initial);
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const loadEventSchedule = async () => {
    if (!eventId) return;
    setBusy(true);
    try {
      const res = await API.get(`/admin/game-engine/schedule/${eventId}`);
      const data = res?.data || [];
      setSchedules(data);

      const eventNos = {};
      const times = {};
      data.forEach((item) => {
        eventNos[item.schedule_key] = item.event_number || "";
        times[item.schedule_key] = item.scheduled_time || "";
      });
      setSchedEventNumbers(eventNos);
      setSchedTimes(times);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load event schedule.", "error");
    } finally {
      setBusy(false);
    }
  };

  const exportScheduleExcel = () => {
    if (!schedules.length) return;

    let csv = "Event No,Scheduled Time,Discipline,Classification,Gender,Players Count,Repeated Players\n";
    schedules.forEach((item) => {
      const eventNo = schedEventNumbers[item.schedule_key] || item.event_number || "";
      const time = schedTimes[item.schedule_key] || item.scheduled_time || "";
      const gameName = item.game_name.replace(/"/g, '""');
      const classification = item.classification.replace(/"/g, '""');
      const gender = item.gender;
      const count = `${item.players_count} Registered`;

      const repeatedNames = (item.repeated_players || [])
        .map((p) => `${p.name} (Bib: ${p.bib_no} in ${p.game_name})`)
        .join("; ");
      const repeatedEscaped = repeatedNames.replace(/"/g, '""');

      csv += `"${eventNo}","${time}","${gameName}","${classification}","${gender}","${count}","${repeatedEscaped}"\n`;
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `event_schedule_${eventId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportSchedulePrint = () => {
    if (!schedules.length) return;

    const printWindow = window.open("", "_blank");
    let html = `
      <html>
        <head>
          <title>Event Schedule Planner</title>
          <style>
            body { font-family: sans-serif; padding: 20px; color: #333; }
            h1 { text-align: center; font-size: 20px; margin-bottom: 5px; }
            h2 { text-align: center; font-size: 14px; color: #666; margin-top: 0; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .font-mono { font-family: monospace; }
            .repeated-list { font-size: 11px; color: #555; }
          </style>
        </head>
        <body>
          <h1>Event Schedule Planner</h1>
          <h2>Event ID: ${eventId}</h2>
          <table>
            <thead>
              <tr>
                <th>Event No</th>
                <th>Scheduled Time</th>
                <th>Discipline</th>
                <th>Classification</th>
                <th>Gender</th>
                <th>Players Count</th>
                <th>Repeated Players</th>
              </tr>
            </thead>
            <tbody>
    `;

    schedules.forEach((item) => {
      const eventNo = schedEventNumbers[item.schedule_key] || item.event_number || "-";
      const time = schedTimes[item.schedule_key] || item.scheduled_time || "-";
      const repeatedText = (item.repeated_players || [])
        .map((p) => `${p.name} (Bib: ${p.bib_no} in ${p.game_name})`)
        .join("<br/>") || "-";

      html += `
        <tr>
          <td class="font-mono" style="font-weight: bold;">${eventNo}</td>
          <td class="font-mono">${time}</td>
          <td style="font-weight: bold;">${item.game_name}</td>
          <td style="color: #4f46e5; font-weight: bold;">${item.classification}</td>
          <td>${item.gender}</td>
          <td>${item.players_count} Registered</td>
          <td class="repeated-list">${repeatedText}</td>
        </tr>
      `;
    });

    html += `
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const saveEventSchedule = async () => {
    if (!eventId) return;
    setBusy(true);
    try {
      const payload = schedules.map((item) => ({
        game_id: item.game_id,
        gender: item.gender,
        event_number: schedEventNumbers[item.schedule_key] || "",
        scheduled_time: schedTimes[item.schedule_key] || "",
      }));
      await API.post("/admin/game-engine/schedule/save", {
        event_id: Number(eventId),
        schedules: payload
      });
      Swal.fire("Saved", "Event schedule updated successfully.", "success");
      loadEventSchedule();
    } catch (e) {
      console.error(e);
      Swal.fire("Error", e?.response?.data?.message || e?.message || "Failed to save event schedule.", "error");
    } finally {
      setBusy(false);
    }
  };

  const loadClassificationCounts = async (targetGender, overrideGameId) => {
    const targetGameId = overrideGameId !== undefined ? overrideGameId : gameId;
    if (!eventId || !targetGameId) return;
    try {
      const activeG = targetGender || hubGender;
      const genders = [];
      if (activeG === "male") genders.push("male");
      if (activeG === "female") genders.push("female");
      const res = await API.get("/admin/game-engine/classification-counts", {
        params: {
          event_id: eventId,
          game_id: targetGameId,
          genders: genders.length ? genders : ["male", "female"],
        },
      });
      const data = res?.data || [];
      const counts = {};
      data.forEach((item) => {
        if (item.classification_id !== undefined && item.classification_id !== null) {
          const val = Number(item.applied_count || 0);
          counts[item.classification_id] = val;
          counts[String(item.classification_id)] = val;
          counts[Number(item.classification_id)] = val;
        }
      });
      setClassCounts(counts);
    } catch (e) {
      console.error("Failed to load classification counts:", e);
    }
  };

  const autoFillManual = () => {
    const cap = Number(componentCount) || 8;
    const next = { ...manualByReg };
    const usesLanes = terms.positionLabel === "Lane";

    const needsGuide = (r) => {
      return usesLanes && (!!r.guide_runner_required || requiresGuideRunnerForClassification(r.classification));
    };

    let groupsOfCandidates = [];
    if (combineCategories) {
      groupsOfCandidates = [candidates];
    } else {
      const groups = new Map();
      candidates.forEach((r) => {
        const classKey = String(r.classification_id ?? r.classification ?? "open");
        if (!groups.has(classKey)) groups.set(classKey, []);
        groups.get(classKey).push(r);
      });
      groupsOfCandidates = Array.from(groups.values());
    }

    let currentHeat = 1;
    let occupiedLanes = new Set();

    groupsOfCandidates.forEach((groupCandidates) => {
      if (!combineCategories) {
        currentHeat = 1;
        occupiedLanes = new Set();
      } else if (occupiedLanes.size > 0) {
        currentHeat += 1;
        occupiedLanes = new Set();
      }

      groupCandidates.forEach((r) => {
        const key = `${r.registration_id}_${r.game_id || 0}`;
        const isBlind = needsGuide(r);

        let assignedHeat = currentHeat;
        let assignedLane = null;

        while (assignedLane === null) {
          if (isBlind) {
            // Find first free odd lane where lane and lane + 1 are free and <= cap
            for (let l = 1; l <= cap - 1; l += 2) {
              if (!occupiedLanes.has(l) && !occupiedLanes.has(l + 1)) {
                assignedLane = l;
                occupiedLanes.add(l);
                occupiedLanes.add(l + 1);
                break;
              }
            }
          } else {
            // Find first free lane <= cap
            for (let l = 1; l <= cap; l++) {
              if (!occupiedLanes.has(l)) {
                assignedLane = l;
                occupiedLanes.add(l);
                break;
              }
            }
          }

          if (assignedLane === null) {
            // Current heat is full, move to next heat
            currentHeat += 1;
            assignedHeat = currentHeat;
            occupiedLanes = new Set();
          }
        }

        next[key] = { heat_number: String(assignedHeat), lane_number: String(assignedLane) };
      });
    });

    setManualByReg(next);
  };

  const saveManualHeats = async () => {
    const assignments = candidates
      .map((r) => {
        const key = `${r.registration_id}_${r.game_id || 0}`;
        const entry = manualByReg[key] || {};
        return {
          registration_id: Number(r.registration_id),
          game_id: Number(r.game_id || 0),
          heat_number: Number(entry.heat_number || 0),
          lane_number: Number(entry.lane_number || 0),
        };
      })
      .filter((r) => r.registration_id > 0 && r.heat_number > 0 && r.lane_number > 0);

    if (!assignments.length) {
      Swal.fire("Warning", "Enter heat and lane assignments first.", "warning");
      return;
    }

    setBusy(true);
    try {
      const genders = hubGender === "all" ? ["male", "female"] : [hubGender];
      await API.post("/admin/game-engine/heats/manual", {
        event_id: Number(eventId),
        game_id: gameId === "all" ? "all" : Number(gameId),
        combine_categories: combineCategories,
        age_category: ageCategory,
        assignments,
        genders,
        classification_ids: selectedClassIds.length > 0 ? selectedClassIds.map(Number) : null,
      });
      Swal.fire("Success", "Manual heats saved successfully.", "success");
      loadGeneratedScoreSheets();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.message || "Failed to save manual heats.";
      Swal.fire("Error", errorMsg, "error");
    } finally {
      setBusy(false);
    }
  };

  // Tab 5: Revocation & Certificates Log
  const loadIssuedCertificates = async () => {
    if (!eventId) return;
    try {
      const res = await API.fetchIssuedCertificates({ event_id: eventId });
      setIssuedCertificates(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadDistrictMedals = async () => {
    if (!eventId) {
      setDistrictMedals([]);
      return;
    }
    try {
      const res = await API.get(`/admin/medal-tally/${eventId}`);
      setDistrictMedals(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error(err);
      setDistrictMedals([]);
    }
  };

  const districtMedalTotals = districtMedals.reduce(
    (acc, row) => ({
      gold: acc.gold + toInt(row.gold),
      silver: acc.silver + toInt(row.silver),
      bronze: acc.bronze + toInt(row.bronze),
      participation: acc.participation + toInt(row.participation),
    }),
    { gold: 0, silver: 0, bronze: 0, participation: 0 }
  );

  const handleDistrictMedalsPdf = () => {
    if (!districtMedals.length) {
      Swal.fire("No data", "No district medal summary is available yet.", "info");
      return;
    }
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const eventName = selectedEvent?.title || "Selected Event";
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, 842, 98, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("PARASPORTS ASSOCIATION OF ANDHRA PRADESH", 40, 38);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`District Medal & Participation Summary - ${eventName}`, 40, 62);
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 84);

    autoTable(doc, {
      startY: 120,
      head: [["District", "Gold", "Silver", "Bronze", "Participation", "Total"]],
      body: districtMedals.map((row) => [
        row.district || "Unknown",
        toInt(row.gold),
        toInt(row.silver),
        toInt(row.bronze),
        toInt(row.participation),
        toInt(row.gold) + toInt(row.silver) + toInt(row.bronze) + toInt(row.participation),
      ]),
      foot: [[
        "Total",
        districtMedalTotals.gold,
        districtMedalTotals.silver,
        districtMedalTotals.bronze,
        districtMedalTotals.participation,
        districtMedalTotals.gold + districtMedalTotals.silver + districtMedalTotals.bronze + districtMedalTotals.participation,
      ]],
      theme: "grid",
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: "bold" },
      footStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      styles: { fontSize: 9, cellPadding: 7, lineColor: [226, 232, 240], lineWidth: 0.5 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 230 } },
    });

    doc.save(`district_medal_summary_${eventId}.pdf`);
  };

  const handleDistrictMedalsPrint = () => {
    if (!districtMedals.length) {
      Swal.fire("No data", "No district medal summary is available yet.", "info");
      return;
    }
    const eventName = selectedEvent?.title || "Selected Event";
    const rows = districtMedals.map((row, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td class="district">${row.district || "Unknown"}</td>
        <td>${toInt(row.gold)}</td>
        <td>${toInt(row.silver)}</td>
        <td>${toInt(row.bronze)}</td>
        <td>${toInt(row.participation)}</td>
        <td>${toInt(row.gold) + toInt(row.silver) + toInt(row.bronze) + toInt(row.participation)}</td>
      </tr>`).join("");
    const total = districtMedalTotals.gold + districtMedalTotals.silver + districtMedalTotals.bronze + districtMedalTotals.participation;
    const win = window.open("", "_blank", "width=1100,height=800");
    if (!win) return;
    win.document.write(`<!doctype html><html><head><title>District Medal Summary</title><style>
      @page { size: A4 landscape; margin: 16mm; }
      body { font-family: Arial, sans-serif; color: #0f172a; margin: 0; background: #f8fafc; }
      .sheet { background: white; min-height: 100vh; padding: 28px; border-top: 12px solid #4f46e5; }
      .hero { background: linear-gradient(135deg, #0f172a, #1d4ed8); color: white; padding: 24px 28px; border-radius: 18px; display: flex; justify-content: space-between; gap: 24px; }
      .hero h1 { margin: 0; font-size: 22px; letter-spacing: 0; }
      .hero p { margin: 7px 0 0; font-size: 13px; opacity: .88; }
      .chips { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 18px 0; }
      .chip { border: 1px solid #e2e8f0; border-radius: 14px; padding: 12px; background: #ffffff; }
      .chip span { display: block; color: #64748b; font-size: 10px; font-weight: 700; text-transform: uppercase; }
      .chip strong { display: block; margin-top: 4px; font-size: 22px; }
      table { width: 100%; border-collapse: collapse; overflow: hidden; border-radius: 14px; }
      th { background: #4f46e5; color: white; text-align: left; font-size: 11px; text-transform: uppercase; padding: 10px; }
      td { padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
      tr:nth-child(even) td { background: #f8fafc; }
      .district { font-weight: 700; }
      tfoot td { background: #0f172a !important; color: white; font-weight: 700; }
      @media print { body { background: white; } .sheet { padding: 0; border-top: none; } }
    </style></head><body><main class="sheet">
      <section class="hero"><div><h1>District Medal & Participation Summary</h1><p>${eventName}</p></div><p>Generated ${new Date().toLocaleString()}</p></section>
      <section class="chips">
        <div class="chip"><span>Gold</span><strong>${districtMedalTotals.gold}</strong></div>
        <div class="chip"><span>Silver</span><strong>${districtMedalTotals.silver}</strong></div>
        <div class="chip"><span>Bronze</span><strong>${districtMedalTotals.bronze}</strong></div>
        <div class="chip"><span>Participation</span><strong>${districtMedalTotals.participation}</strong></div>
      </section>
      <table><thead><tr><th>#</th><th>District</th><th>Gold</th><th>Silver</th><th>Bronze</th><th>Participation</th><th>Total</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><td colspan="2">Total</td><td>${districtMedalTotals.gold}</td><td>${districtMedalTotals.silver}</td><td>${districtMedalTotals.bronze}</td><td>${districtMedalTotals.participation}</td><td>${total}</td></tr></tfoot></table>
    </main></body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  };
  const handleRevokeCertificate = async (id) => {
    const { value: reason } = await Swal.fire({
      title: "Revoke Certificate",
      input: "text",
      inputLabel: "Reason for revocation",
      inputPlaceholder: "Enter reason...",
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return "Reason is required to revoke!";
        }
      },
    });
    if (reason) {
      const { value: password } = await Swal.fire({
        title: "Confirm Password",
        input: "password",
        inputLabel: "Please enter your password to confirm revocation",
        inputPlaceholder: "Enter password",
        showCancelButton: true,
        confirmButtonText: "Revoke",
        confirmButtonColor: "#dc2626",
        inputValidator: (v) => !v && "Password is required!"
      });
      if (!password) return;

      try {
        await API.revokeCertificate(id, reason, password);
        Swal.fire("Revoked", "Certificate has been revoked.", "success");
        loadIssuedCertificates();
      } catch (err) {
        const errorMsg = err?.response?.data?.message || err?.message || "Failed to revoke certificate.";
        Swal.fire("Error", errorMsg, "error");
      }
    }
  };

  const handleReissueCertificate = async (id) => {
    const confirm = await Swal.fire({
      title: "Reissue Certificate",
      text: "This will restore the revoked certificate and mark it active again.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Reissue",
    });
    if (!confirm.isConfirmed) return;
    try {
      await API.post(`/admin/results/certificates/${id}/reissue`);
      Swal.fire("Reissued", "Certificate has been reissued and is active again.", "success");
      loadIssuedCertificates();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.message || "Failed to reissue certificate.";
      Swal.fire("Error", errorMsg, "error");
    }
  };

  // Certificate printing
  const handleDownloadCertificate = async (regId, regenerate = false) => {
    try {
      let keep_signatures = false;
      if (regenerate) {
        const { value: action } = await Swal.fire({
            title: 'Reprint Certificate',
            input: 'radio',
            inputOptions: {
                'correction': 'Correction (update data, keep old signatures)',
                'regenerate': 'Regenerate (update data, use current signatures)'
            },
            inputValue: 'correction',
            showCancelButton: true,
            confirmButtonText: 'Proceed',
            confirmButtonColor: '#0d3373',
        });
        if (!action) return;
        keep_signatures = action === 'correction';
        Swal.fire({ title: "Generating certificate...", didOpen: () => Swal.showLoading() });
      }
      const { blob, filename } = await API.downloadRegistrationCertificate(regId, { regenerate, keep_signatures });
      if (regenerate) Swal.close();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (regenerate) Swal.close();
      const errorMsg = err?.response?.data?.message || err?.message || "Failed to generate certificate.";
      Swal.fire("Error", errorMsg, "error");
    }
  };

  const handleBulkDownload = async () => {
    if (!eventId) return;
    try {
      Swal.fire({ title: "Generating bulk ZIP...", didOpen: () => Swal.showLoading() });
      await API.downloadEventCertificatesZip(eventId);
      Swal.close();
      Swal.fire("Success", "Certificates zip downloaded.", "success");
      loadIssuedCertificates();
    } catch (err) {
      Swal.close();
      Swal.fire("Error", "Failed to generate bulk ZIP.", "error");
    }
  };

  // Live match control incremental bumps
  const handleBumpLiveScore = (side, delta) => {
    setLiveScore((prev) => ({
      ...prev,
      [side]: Math.max(0, Number(prev[side] || 0) + delta),
    }));
  };

  const handleSetWinner = (side) => {
    const winnerId = side === "left" ? liveLeftId : liveRightId;
    setWinnerByMatch((prev) => ({ ...prev, [liveMatchId]: winnerId }));
  };

  const handleSyncLiveScore = () => {
    if (!liveMatchId) return;
    const scoreText = `${liveScore.left}-${liveScore.right}`;
    setScoreByMatch((prev) => ({ ...prev, [liveMatchId]: scoreText }));
    let winner = "";
    if (liveLeftId && !liveRightId) winner = liveLeftId;
    if (liveRightId && !liveLeftId) winner = liveRightId;
    if (!winner && liveScore.left > liveScore.right) winner = liveLeftId;
    if (!winner && liveScore.right > liveScore.left) winner = liveRightId;
    setWinnerByMatch((prev) => ({ ...prev, [liveMatchId]: winner }));
    Swal.fire("Synced", "Score synced to forms.", "success");
  };

  const handleSaveLiveResult = async (next = false) => {
    const match = matches.find((m) => String(m.id) === String(liveMatchId));
    if (!match) return;
    const winner = winnerByMatch[liveMatchId];
    const score = `${liveScore.left}-${liveScore.right}`;
    if (!winner) {
      Swal.fire("Warning", "Select winner first.", "warning");
      return;
    }
    await handleSaveMatchResult(match, winner, score);
    if (next) {
      const idx = matches.findIndex((m) => String(m.id) === String(liveMatchId));
      if (idx !== -1 && idx + 1 < matches.length) {
        setLiveMatchId(String(matches[idx + 1].id));
      }
    }
  };

  // Parse details for incremental match views
  const liveLeftId = useMemo(() => {
    const m = matches.find((x) => String(x.id) === String(liveMatchId));
    return m ? String(isTeamMode ? m.team1_id || "" : m.player1_reg_id || "") : "";
  }, [matches, liveMatchId, isTeamMode]);

  const liveRightId = useMemo(() => {
    const m = matches.find((x) => String(x.id) === String(liveMatchId));
    return m ? String(isTeamMode ? m.team2_id || "" : m.player2_reg_id || "") : "";
  }, [matches, liveMatchId, isTeamMode]);

  const liveLeftLabel = useMemo(() => {
    const m = matches.find((x) => String(x.id) === String(liveMatchId));
    return m ? (isTeamMode ? m.team1_name : m.player1_name) || "BYE" : "Competitor Left";
  }, [matches, liveMatchId, isTeamMode]);

  const liveRightLabel = useMemo(() => {
    const m = matches.find((x) => String(x.id) === String(liveMatchId));
    return m ? (isTeamMode ? m.team2_name : m.player2_name) || "BYE" : "Competitor Right";
  }, [matches, liveMatchId, isTeamMode]);

  // Effects
  useEffect(() => {
    loadEvents(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    if (eventId) {
      loadCheckinData();
      loadResultsContext();
      loadIssuedCertificates();
      loadDistrictMedals();
      loadClassificationCompletion();
      loadGeneratedScoreSheets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (eventId && activeTab === "engine") {
      loadClassificationCompletion();
      loadGeneratedScoreSheets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, activeTab]);

  // Re-load checkin table whenever the hub gender filter changes
  useEffect(() => {
    if (eventId) {
      loadCheckinData(hubGender);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubGender]);

  useEffect(() => {
    if (gameId && (activeTab === "results" || activeTab === "leaderboard" || activeTab === "certificates")) {
      loadPlayersForResults();
      loadTeamsAndMatches();
      loadClassificationCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, selectedClassIds, hubGender, activeTab]);

  useEffect(() => {
    if (eventId && activeTab === "schedule") {
      loadEventSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, activeTab]);

  useEffect(() => {
    if (gameId && activeTab === "engine") {
      loadCandidates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, eventId, hubGender, ageCategory, combineCategories, selectedClassIds, activeTab]);

  useEffect(() => {
    if (activeTab === "results") {
      loadLiveSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (gameId && (activeTab === "results" || activeTab === "leaderboard" || activeTab === "certificates")) {
      loadAllPlayersForStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, eventId, activeTab]);

  return (
    <>
      <style>{`
        @media print {
          @page { size: portrait; margin: 10mm; }
          body { font-size: 11pt; color: #000; background: #fff; }
          header, nav, button, .no-print { display: none !important; }
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #000 !important; padding: 6px !important; text-align: center !important; }
          th { background-color: #f1f5f9 !important; -webkit-print-color-adjust: exact; }
        }
      `}</style>
      <Navbar />
      <div className="min-h-screen bg-slate-50 pb-12">
        <div className="max-w-[1440px] mx-auto p-4 sm:p-6 lg:p-8">
          {/* Main Scope Control Panel */}
          <header className="mb-6 bg-white rounded-3xl p-6 border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">Unified Operations Desk</span>
              <h1 className="text-2xl font-bold text-slate-800">Games Operations Hub</h1>
              <p className="text-slate-500 text-sm mt-0.5">Control live matches, check-ins, scores, brackets, and printings from one window.</p>
            </div>
            <div className="flex flex-wrap gap-3 items-center w-full md:w-auto">
              <div className="flex-1 md:flex-initial min-w-[150px]">
                <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>
              <div className="flex-1 md:flex-initial min-w-[280px]">
                <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Active Event</label>
                <SearchableSelect
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="">-- Choose Event --</option>
                  {events.map((e) => (
                    <option key={e.id} value={e.id}>{e.title}</option>
                  ))}
                </SearchableSelect>
              </div>
              <div className="flex-1 md:flex-initial min-w-[140px]">
                <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Gender</label>
                <SearchableSelect
                  value={hubGender}
                  onChange={(e) => setHubGender(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="all">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </SearchableSelect>
              </div>
            </div>
          </header>

          {/* Unified Hub Navigation Tabs */}
          <div className="mb-6 flex gap-1.5 overflow-x-auto bg-slate-200/60 p-1.5 rounded-2xl">
            {HUB_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${activeTab === tab.id
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "text-slate-600 hover:bg-slate-100/50"
                  }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab 1: Live Matches & Check-in */}
          {activeTab === "checkin" && (
            <div className="space-y-6 animate-fadeIn">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Total Competitors", value: checkinData.summary?.total_players || 0, color: "text-slate-900" },
                  { label: "Checked In", value: checkinData.summary?.approved || 0, color: "text-emerald-600" },
                  { label: "No Shows", value: checkinData.summary?.rejected || 0, color: "text-rose-600" },
                  { label: "Pending Check-In", value: checkinData.summary?.pending || 0, color: "text-amber-500" },
                ].map((stat, idx) => (
                  <div key={idx} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                    <p className="text-xs font-bold uppercase text-slate-400">{stat.label}</p>
                    <p className={`text-3xl font-extrabold mt-1 ${stat.color}`}>{stat.value}</p>
                  </div>
                ))}
              </div>

              {/* Download & Print Desk */}
              <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                <div className="mb-4">
                  <h2 className="text-lg font-bold text-slate-800">Download & Print Desk</h2>
                  <p className="text-sm text-slate-500">Generate on-ground operational packets.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Attendance Pack */}
                  <div className="border rounded-2xl p-4 bg-slate-50">
                    <h3 className="font-bold text-slate-700 flex items-center gap-1.5"><FaClipboardList /> Attendance Sheet</h3>
                    <p className="text-xs text-slate-500 mt-1">Download attendance logs for matching player classifications.</p>
                    <div className="mt-4 flex gap-2">
                      <button onClick={() => downloadAttendanceSheet("pdf")} className="flex-1 bg-white hover:bg-slate-100 border text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1"><FaFilePdf /> PDF</button>
                      <button onClick={() => downloadAttendanceSheet("excel")} className="flex-1 bg-white hover:bg-slate-100 border text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1"><FaFileExcel /> Excel</button>
                      <button onClick={() => printGameDayFile(`/admin/game-day/attendance-sheet/${eventId}`)} className="flex-1 bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1"><FaPrint /> Print</button>
                    </div>
                  </div>
                  {/* Event list */}
                  <div className="border rounded-2xl p-4 bg-slate-50">
                    <h3 className="font-bold text-slate-700 flex items-center gap-1.5"><FaListUl /> Event List</h3>
                    <p className="text-xs text-slate-500 mt-1">Get summary rosters of registered participants.</p>
                    <div className="mt-4 flex gap-2">
                      <button onClick={() => downloadEventList("pdf")} className="flex-1 bg-white hover:bg-slate-100 border text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1"><FaFilePdf /> PDF</button>
                      <button onClick={() => downloadEventList("excel")} className="flex-1 bg-white hover:bg-slate-100 border text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1"><FaFileExcel /> Excel</button>
                      <button onClick={() => printGameDayFile(`/admin/game-day/event-list/${eventId}`)} className="flex-1 bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1"><FaPrint /> Print</button>
                    </div>
                  </div>
                  {/* BIB cards if athletics */}
                  {isAthleticsEvent && (
                    <div className="border rounded-2xl p-4 bg-slate-50">
                      <h3 className="font-bold text-slate-700 flex items-center gap-1.5"><FaIdCard /> Athletics BIB Cards</h3>
                      <p className="text-xs text-slate-500 mt-1">Print competition bib numbers for field athletes.</p>
                      <div className="mt-4 flex gap-2">
                        <button onClick={() => downloadAthleticsBibCards("pdf")} className="flex-1 bg-white hover:bg-slate-100 border text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1"><FaFilePdf /> PDF</button>
                        <button onClick={() => downloadAthleticsBibCards("excel")} className="flex-1 bg-white hover:bg-slate-100 border text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1"><FaFileExcel /> Excel</button>
                        <button onClick={() => printGameDayFile(`/admin/game-day/athletics-bib-cards/${eventId}`)} className="flex-1 bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1"><FaPrint /> Print</button>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* Attendance checklist */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
                  <h2 className="font-bold text-slate-800">Athlete Attendance Check List</h2>
                  <div className="flex flex-wrap gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Search competitor name..."
                      value={checkinFilters.q}
                      onChange={(e) => setCheckinFilters({ ...checkinFilters, q: e.target.value })}
                      className="border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      onClick={loadCheckinData}
                      className="px-4 py-2 rounded-xl bg-slate-800 text-white font-bold text-sm"
                    >
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-600 uppercase text-xs font-bold border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-4">Competitor</th>
                        <th className="px-6 py-4">Bib No</th>
                        <th className="px-6 py-4">Gender</th>
                        <th className="px-6 py-4">District</th>
                        <th className="px-6 py-4">Classification</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {checkinData.participants.length > 0 ? (
                        checkinData.participants.map((p) => (
                          <tr key={p.registration_id} className="hover:bg-slate-50/50">
                            <td className="px-6 py-4 font-bold text-slate-800">{p.player_name}</td>
                            <td className="px-6 py-4 font-mono text-slate-600">{p.bib_no || p.registration_id || "-"}</td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${String(p.gender || "").toLowerCase() === "male"
                                ? "bg-blue-100 text-blue-700"
                                : String(p.gender || "").toLowerCase() === "female"
                                  ? "bg-pink-100 text-pink-700"
                                  : "bg-slate-100 text-slate-600"
                                }`}>
                                {String(p.gender || "-").charAt(0).toUpperCase() + String(p.gender || "").slice(1) || "-"}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-slate-600">{p.district}</td>
                            <td className="px-6 py-4 font-semibold text-indigo-600">{p.classification || "Open"}</td>
                            <td className="px-6 py-4">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${p.status === "Approved" ? "bg-emerald-100 text-emerald-700" :
                                p.status === "Rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"
                                }`}>
                                {p.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right flex gap-1.5 justify-end">
                              <button
                                onClick={() => handleUpdateStatus(p.registration_id, "Approved")}
                                className="px-2.5 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 text-xs font-bold"
                                title="Approve"
                              >
                                <FaCheck />
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(p.registration_id, "Rejected")}
                                className="px-2.5 py-1.5 rounded-lg bg-rose-100 text-rose-700 hover:bg-rose-200 text-xs font-bold"
                                title="Reject"
                              >
                                <FaTimes />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-12 text-slate-400 italic">
                            {!eventId ? "Select an event from the header to view competitors." : "No competitors found matching the active filters."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Game Engine Setup */}
          {activeTab === "engine" && (
            <div className="space-y-6 animate-fadeIn">
              {classificationLockInfo.isLocked && !adminForceUnlock && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-3xl p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-amber-100 text-amber-700 rounded-2xl text-2xl font-bold">⚠️</div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-amber-900">Game Engine Setup Locked</h3>
                      <p className="text-sm text-amber-800 mt-1">
                        <strong>{classificationLockInfo.unclassifiedCount}</strong> competitor(s) registered for this event have not completed classification allotment. Official rules require completing classification allotment for all players before generating heats or event schedules.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-3 items-center">
                        <a
                          href="/admin/classification-allotment"
                          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm transition"
                        >
                          Go to Classification Allotment
                        </a>
                        <label className="flex items-center gap-2 text-xs font-bold text-amber-900 cursor-pointer ml-auto bg-amber-100/80 hover:bg-amber-100 px-3 py-2 rounded-xl border border-amber-200">
                          <input
                            type="checkbox"
                            checked={adminForceUnlock}
                            onChange={(e) => setAdminForceUnlock(e.target.checked)}
                          />
                          Admin Override: Force Unlock Setup
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-6">
                  {/* Engine Mode */}
                  <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                    <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">Active Mode</span>
                    <h2 className="text-lg font-bold text-slate-800 mt-1">{selectedGame ? selectedGame.game_name : "Select a Discipline"}</h2>
                    <div className="mt-2 inline-block px-3 py-1 bg-indigo-50 border border-indigo-100 rounded-lg text-indigo-700 text-xs font-bold capitalize">
                      {engineMode.replace("_", " ")} Setup Mode
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {engineMode === "fixture" && "Supports round/fixture trees. Generate fixture rosters before entry."}
                      {engineMode === "heat" && "Supports running lanes and throwing pits. Assign heats & equipment."}
                      {engineMode === "hybrid" && "Supports both heat assignments and bracket creations."}
                    </p>

                    {/* Target Discipline */}
                    <div className="mt-4">
                      <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Target Discipline</label>
                      <SearchableSelect
                        value={gameId}
                        onChange={(e) => handleGameChange(e.target.value)}
                        className="w-full border border-slate-200 rounded-xl p-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">-- Choose Discipline --</option>
                        <option value="all">All Disciplines</option>
                        {games.map((g) => (
                          <option key={g.id} value={g.id}>{g.game_name}</option>
                        ))}
                      </SearchableSelect>
                    </div>
                  </section>

                  {/* Configurations */}
                  <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm space-y-4">
                    <h3 className="font-bold text-slate-800">Generation Settings</h3>

                    <div className="flex items-center gap-2">
                      <input
                        id="combine-categories"
                        type="checkbox"
                        checked={combineCategories}
                        onChange={(e) => setCombineCategories(e.target.checked)}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor="combine-categories" className="text-xs font-bold text-slate-700">Mix Classifications</label>
                    </div>

                    {combineCategories && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-bold uppercase text-slate-500">Merge Classifications</label>
                          {mergeClassificationIds.length > 0 && (
                            <span className="text-[11px] font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                              Merged: {mergeClassificationIds.reduce((sum, id) => sum + (classCounts[id] || classCounts[String(id)] || classCounts[Number(id)] || 0), 0)} Players
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 p-2 border border-slate-200 rounded-xl max-h-36 overflow-y-auto">
                          {classifications.map((c) => {
                            const count = classCounts[c.id] || classCounts[String(c.id)] || classCounts[Number(c.id)] || 0;
                            return (
                              <label key={c.id} className="flex items-center gap-1 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-0.5 hover:bg-indigo-50">
                                <input
                                  type="checkbox"
                                  checked={mergeClassificationIds.includes(String(c.id))}
                                  onChange={(e) => {
                                    const id = String(c.id);
                                    setMergeClassificationIds((prev) =>
                                      e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
                                    );
                                  }}
                                />
                                <span className="font-semibold text-slate-700">{c.classification} <strong className="text-indigo-600">({count})</strong></span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Age Category</label>
                      <select
                        value={ageCategory}
                        onChange={(e) => setAgeCategory(e.target.value)}
                        className="w-full border border-slate-200 rounded-xl p-2.5 text-sm font-semibold"
                      >
                        <option value="all">All / Combined Age Categories</option>
                        <option value="sub_junior">Sub-Junior (≤ 14 years)</option>
                        <option value="junior">Junior (15 – 17 years)</option>
                        <option value="senior">Senior (≥ 18 years)</option>
                        <option value="combined_sub_junior_junior">Combined: Sub-Junior + Junior</option>
                        <option value="combined_junior_senior">Combined: Junior + Senior</option>
                      </select>
                    </div>

                  {engineMode !== "fixture" && (
                    <div className="mt-3">
                      <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Equipment Capacity</label>
                      <input
                        type="number"
                        min="1"
                        max="32"
                        value={componentCount}
                        onChange={(e) => setComponentCount(e.target.value)}
                        className="w-full border border-slate-200 rounded-xl p-2.5 text-sm"
                      />
                    </div>
                  )}
                </section>

                {/* Setup Commands */}
                <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm space-y-3">
                  <h3 className="font-bold text-slate-800">Engine Actions</h3>
                  <button
                    onClick={runRecommendedSetup}
                    disabled={busy || !gameId}
                    className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <FaProjectDiagram /> Run Recommended Setup
                  </button>
                  <div className="space-y-2">
                    {engineMode !== "fixture" && (
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={generateHeats}
                          disabled={busy || !gameId}
                          className="py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1 disabled:opacity-50"
                        >
                          Generate Heats
                        </button>
                        <button
                          onClick={clearHeats}
                          disabled={busy || !gameId}
                          className="py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl flex items-center justify-center gap-1 disabled:opacity-50"
                        >
                          Clear Heats
                        </button>
                      </div>
                    )}
                    {engineMode !== "heat" && (
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={generateFixtures}
                          disabled={busy || !gameId}
                          className="py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1 disabled:opacity-50"
                        >
                          Generate Fixtures
                        </button>
                        <button
                          onClick={clearFixtures}
                          disabled={busy || !gameId}
                          className="py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl flex items-center justify-center gap-1 disabled:opacity-50"
                        >
                          Clear Fixtures
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={resetResultSheetLocks}
                    disabled={busy || !eventId}
                    className="w-full py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-100 text-xs font-bold rounded-xl flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    Reset Locks
                  </button>
                </section>

                {/* Heats & Score Sheets Downloads Panel */}
                <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm space-y-4">
                  <div>
                    <h3 className="font-bold text-slate-800">Heats & Score Sheets</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Download or print rosters and scoring documents.</p>
                  </div>

                  <div className="space-y-3">
                    {/* Heat Schedule Downloads */}
                    <div className="space-y-1.5">
                      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Heat Schedule (All Games)</span>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => downloadHeatSchedule("pdf")}
                          disabled={!eventId}
                          className="py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                        >
                          <FaFilePdf className="text-red-500" /> PDF
                        </button>
                        <button
                          onClick={() => downloadHeatSchedule("excel")}
                          disabled={!eventId}
                          className="py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                        >
                          <FaFileExcel className="text-emerald-600" /> Excel
                        </button>
                      </div>
                    </div>

                    {/* Score Sheet Downloads */}
                    <div className="space-y-1.5">
                      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Time Slips (Current Game)</span>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => downloadTimeSlips("pdf")} disabled={!eventId || !gameId} className="py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"><FaFilePdf className="text-red-500" /> PDF</button>
                        <button onClick={() => downloadTimeSlips("excel")} disabled={!eventId || !gameId} className="py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"><FaFileExcel className="text-emerald-600" /> Excel</button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Score Sheet (Current Game)</span>
                      <div className="grid grid-cols-3 gap-1.5">
                        <button
                          onClick={() => downloadScoreSheet("pdf")}
                          disabled={!eventId || !gameId}
                          className="py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-[10px] font-bold flex items-center justify-center gap-0.5 disabled:opacity-50"
                        >
                          <FaFilePdf className="text-red-500" /> PDF
                        </button>
                        <button
                          onClick={() => downloadScoreSheet("excel")}
                          disabled={!eventId || !gameId}
                          className="py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-[10px] font-bold flex items-center justify-center gap-0.5 disabled:opacity-50"
                        >
                          <FaFileExcel className="text-emerald-600" /> Excel
                        </button>
                        <button
                          onClick={() => downloadScoreSheet("print")}
                          disabled={!eventId || !gameId}
                          className="py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-100 rounded-lg text-[10px] font-bold flex items-center justify-center gap-0.5 disabled:opacity-50"
                        >
                          <FaPrint /> Print
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              {/* Manual Heat Grid / Fixtures Draws List */}
              <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200 p-6 shadow-sm flex flex-col">
                {engineMode === "fixture" ? (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h3 className="font-bold text-slate-800">Generated Fixtures Draws</h3>
                        <p className="text-xs text-slate-500 mt-0.5">Rosters of generated matches and pairings for brackets.</p>
                      </div>
                      <button
                        onClick={loadTeamsAndMatches}
                        disabled={busy || !gameId}
                        className="px-3 py-1.5 border text-xs font-bold rounded-lg hover:bg-slate-50 disabled:opacity-50"
                      >
                        Refresh Draws
                      </button>
                    </div>

                    {matches.length > 0 ? (
                      <div className="overflow-x-auto flex-1 border rounded-2xl">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
                            <tr>
                              <th className="px-4 py-3">Round</th>
                              <th className="px-4 py-3">Competitor 1</th>
                              <th className="px-4 py-3">Competitor 2</th>
                              <th className="px-4 py-3">Declared Winner</th>
                              <th className="px-4 py-3">Score</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {matches.map((m) => (
                              <tr key={m.id} className="hover:bg-slate-50/50">
                                <td className="px-4 py-3 font-semibold text-slate-500 text-xs">{m.round_name || "Round 1"}</td>
                                <td className="px-4 py-3 font-bold text-slate-800">{isTeamMode ? m.team1_name : m.player1_name || "BYE"}</td>
                                <td className="px-4 py-3 font-bold text-slate-800">{isTeamMode ? m.team2_name : m.player2_name || "BYE"}</td>
                                <td className="px-4 py-3 font-semibold text-xs">
                                  {m.winner_id ? (isTeamMode ? (String(m.winner_id) === String(m.team1_id) ? m.team1_name : m.team2_name) : (String(m.winner_id) === String(m.player1_reg_id) ? m.player1_name : m.player2_name)) : <span className="text-slate-400 italic font-normal">Pending</span>}
                                </td>
                                <td className="px-4 py-3 font-mono text-xs">{m.score || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center py-12 text-slate-400 border border-dashed rounded-2xl">
                        <span className="text-3xl mb-2">🏆</span>
                        <p className="text-sm italic">No match fixtures generated yet for this discipline.</p>
                        <button
                          onClick={generateFixtures}
                          disabled={busy || !gameId}
                          className="mt-3 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg disabled:opacity-50"
                        >
                          Generate Fixtures
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {!candidates.length ? (
                      <div className="flex-1 flex flex-col items-center justify-center py-12 text-slate-400 border border-dashed rounded-2xl">
                        <span className="text-3xl mb-2">📋</span>
                        <p className="text-sm italic">Choose a discipline or load candidates to start.</p>
                        <button
                          onClick={loadCandidates}
                          disabled={busy || !gameId}
                          className="mt-3 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-lg disabled:opacity-50"
                        >
                          Load Candidates
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h3 className="font-bold text-slate-800">Manual Lane Allocation</h3>
                            <p className="text-xs text-slate-500 mt-0.5">Assign competitor heats and lane positions manually.</p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={autoFillManual}
                              disabled={!candidates.length}
                              className="px-3 py-1.5 border text-xs font-bold rounded-lg hover:bg-slate-50 disabled:opacity-50"
                            >
                              Auto Fill Lanes
                            </button>
                            <button
                              onClick={saveManualHeats}
                              disabled={!candidates.length}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg disabled:opacity-50"
                            >
                              Save Allocation
                            </button>
                          </div>
                        </div>

                        <div className="overflow-x-auto flex-1 border rounded-2xl">
                          <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
                              <tr>
                                <th className="px-4 py-3">Registration ID</th>
                                <th className="px-4 py-3">Competitor Name</th>
                                <th className="px-4 py-3">Gender</th>
                                <th className="px-4 py-3">Discipline</th>
                                <th className="px-4 py-3">Classification</th>
                                <th className="px-4 py-3">{terms.shortGroupLabel}</th>
                                <th className="px-4 py-3">{terms.shortPositionLabel}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {candidates.map((r) => {
                                const key = `${r.registration_id}_${r.game_id || 0}`;
                                const val = manualByReg[key] || { heat_number: "", lane_number: "" };
                                return (
                                  <tr key={key} className="hover:bg-slate-50/50">
                                    <td className="px-4 py-3 font-mono font-bold text-slate-700">{r.player_registration_id || "-"}</td>
                                    <td className="px-4 py-3 font-bold text-slate-800">{r.player_name || r.name || "-"}<div className="text-[10px] font-normal text-slate-500">Father: {r.father_name || "-"}</div></td>
                                    <td className="px-4 py-3 capitalize text-xs text-slate-500">{r.gender || "-"}</td>
                                    <td className="px-4 py-3 text-slate-500 text-xs">{r.game_name}</td>
                                    <td className="px-4 py-3 text-indigo-600 font-semibold">{r.classification || "Open"}</td>
                                    <td className="px-4 py-3">
                                      <input
                                        type="number"
                                        placeholder={terms.groupLabel}
                                        value={val.heat_number || ""}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setManualByReg((prev) => ({
                                            ...prev,
                                            [key]: { ...prev[key], heat_number: v },
                                          }));
                                        }}
                                        className="w-16 border rounded px-2 py-1 text-center"
                                      />
                                    </td>
                                    <td className="px-4 py-3">
                                      <input
                                        type="number"
                                        placeholder={terms.positionLabel}
                                        value={val.lane_number || ""}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setManualByReg((prev) => ({
                                            ...prev,
                                            [key]: { ...prev[key], lane_number: v },
                                          }));
                                        }}
                                        className="w-16 border rounded px-2 py-1 text-center"
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Dedicated Already Generated Events Log & Download Desk */}
            <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm space-y-4 mt-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                    Engine Log & Documents Desk
                  </span>
                  <h3 className="font-bold text-slate-800 text-lg mt-1">Already Generated Events & Score Sheets</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Overview of all generated discipline setups, heat schedules, score sheets, and document downloads.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-700 bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200">
                    Total Generated: <strong className="text-indigo-600">{generatedScoreSheets.length}</strong>
                  </span>
                  <button
                    onClick={loadGeneratedScoreSheets}
                    className="p-2 text-slate-500 hover:text-indigo-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                    title="Refresh List"
                  >
                    <FaRedo className="text-xs" />
                  </button>
                  {generatedScoreSheets.length > 0 && (
                    <button
                      onClick={handleClearAllGeneratedEvents}
                      className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-colors"
                      title="Clear All Generated Heats & Fixtures"
                    >
                      <FaTrash className="text-xs" /> Clear All Heats
                    </button>
                  )}
                </div>
              </div>

              {generatedScoreSheets.length > 0 ? (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-800 text-white font-bold">
                      <tr>
                        <th className="px-4 py-3">Discipline / Game</th>
                        <th className="px-4 py-3">Gender</th>
                        <th className="px-4 py-3">Classifications</th>
                        <th className="px-4 py-3">Setup Mode</th>
                        <th className="px-4 py-3">Generated Date</th>
                        <th className="px-4 py-3 text-right">Actions & Documents</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {generatedScoreSheets.map((item, idx) => {
                        const classNames = (item.classification_names || []).join(", ") || (item.mode === "combined" ? "Mixed" : "All");
                        return (
                          <tr key={item.id || idx} className="hover:bg-slate-50/80 transition-colors">
                            <td className="px-4 py-3 font-bold text-slate-800">
                              {item.game_name || "All Games"}
                            </td>
                            <td className="px-4 py-3 font-semibold capitalize text-slate-600">
                              {item.gender || "All"}
                            </td>
                            <td className="px-4 py-3 font-semibold">
                              <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded-md font-bold">
                                {classNames}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-md font-bold text-[10px] uppercase ${
                                item.mode === "fixture"
                                  ? "bg-amber-50 text-amber-700 border border-amber-200"
                                  : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              }`}>
                                {item.mode === "fixture" ? "Knockout Fixtures" : (item.mode === "combined" ? "Combined Heats" : "Individual Heats")}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-500 font-mono text-[11px]">
                              {item.updated_at ? new Date(item.updated_at).toLocaleString() : "-"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  onClick={() => {
                                    downloadHeatSchedule("pdf", item.game_id);
                                  }}
                                  className="px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg font-bold text-[11px] flex items-center gap-1"
                                  title="Download Heat Schedule PDF"
                                >
                                  <FaFilePdf /> Heats PDF
                                </button>
                                <button
                                  onClick={() => {
                                    downloadScoreSheet("pdf", item.game_id);
                                  }}
                                  className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg font-bold text-[11px] flex items-center gap-1"
                                  title="Download Score Sheet PDF"
                                >
                                  <FaFilePdf /> Score Sheet
                                </button>
                                <button
                                  onClick={() => {
                                    downloadScoreSheet("excel", item.game_id);
                                  }}
                                  className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg font-bold text-[11px] flex items-center gap-1"
                                  title="Download Excel Roster"
                                >
                                  <FaFileExcel /> Excel
                                </button>
                                <button
                                  onClick={() => handleClearGeneratedEvent(item)}
                                  className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold text-[11px] flex items-center gap-1 shadow-sm transition-colors"
                                  title="Clear heats and reset setup for this generated event"
                                >
                                  <FaTrash className="text-[10px]" /> Clear Heats
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <span className="text-3xl block mb-2">📋</span>
                  <p className="text-sm font-semibold text-slate-600">No events or heats generated yet for this tournament.</p>
                  <p className="text-xs text-slate-400 mt-1">Select a game above and click "Run Recommended Setup" or "Generate Heats" to start.</p>
                </div>
              )}
            </section>
          </div>
        )}

          {/* Tab 2.5: Event Schedule */}
          {activeTab === "schedule" && (
            <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm flex flex-col space-y-6 animate-fadeIn">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h3 className="font-bold text-slate-800 text-lg">Event Schedule Planner</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Assign event order numbers and conduct times to games and classifications.</p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    onClick={exportSchedulePrint}
                    disabled={!schedules.length}
                    className="px-4 py-2 border hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl disabled:opacity-50 flex items-center gap-1.5"
                  >
                    🖨️ Print / PDF
                  </button>
                  <button
                    onClick={exportScheduleExcel}
                    disabled={!schedules.length}
                    className="px-4 py-2 border hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl disabled:opacity-50 flex items-center gap-1.5"
                  >
                    📊 Excel
                  </button>
                  <button
                    onClick={saveEventSchedule}
                    disabled={busy || !schedules.length}
                    className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl disabled:opacity-50 animate-pulse-slow"
                  >
                    Save Schedule
                  </button>
                </div>
              </div>

              {schedules.length > 0 ? (
                <div className="overflow-x-auto border rounded-2xl">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3">Event No</th>
                        <th className="px-4 py-3">Scheduled Time</th>
                        <th className="px-4 py-3">Discipline</th>
                        <th className="px-4 py-3">Classification</th>
                        <th className="px-4 py-3">Gender</th>
                        <th className="px-4 py-3">Competitors</th>
                        <th className="px-4 py-3">Repeated in Other Games</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {schedules.map((item) => (
                        <tr key={item.schedule_key} className="hover:bg-slate-50/50">
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              placeholder="e.g. 101"
                              value={schedEventNumbers[item.schedule_key] || ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSchedEventNumbers((prev) => ({
                                  ...prev,
                                  [item.schedule_key]: val,
                                }));
                              }}
                              className="w-24 border rounded-lg px-2.5 py-1 text-center font-bold font-mono"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="time"
                              value={schedTimes[item.schedule_key] || ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSchedTimes((prev) => ({
                                  ...prev,
                                  [item.schedule_key]: val,
                                }));
                              }}
                              className="w-32 border rounded-lg px-2.5 py-1 text-center font-mono"
                            />
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-800">{item.game_name}</td>
                          <td className="px-4 py-3 text-indigo-600 font-semibold">{item.classification || "Open"}</td>
                          <td className="px-4 py-3 capitalize text-xs text-slate-500">{item.gender}</td>
                          <td className="px-4 py-3 text-slate-500 font-bold">{item.players_count} Registered</td>
                          <td className="px-4 py-3">
                            {item.repeated_players && item.repeated_players.length > 0 ? (
                              <div className="max-w-xs text-[11px] space-y-1">
                                {item.repeated_players.map((p, idx) => (
                                  <div key={idx} className="bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-0.5 leading-snug">
                                    <strong>{p.name}</strong> (Bib: {p.bib_no}) <br />
                                    <span className="text-[9px] text-amber-600 font-medium">also in: {p.game_name}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400 italic">None</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400 border border-dashed rounded-2xl">
                  <span className="text-4xl mb-2">📅</span>
                  <p className="text-sm italic">No registrations found in this event to schedule.</p>
                </div>
              )}
            </div>
          )}

          {/* Tab 3: Result Entry Desk */}
          {activeTab === "results" && (
            <div className="space-y-6 animate-fadeIn">
              <style>{`
                @media print {
                  body * {
                    visibility: hidden;
                  }
                  #print-scoring-sheet, #print-scoring-sheet * {
                    visibility: visible;
                  }
                  #print-scoring-sheet {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                    box-shadow: none !important;
                    border: none !important;
                    padding: 0 !important;
                  }
                  #print-scoring-sheet .flex.items-center.gap-2,
                  #print-scoring-sheet button,
                  #print-scoring-sheet input[type="button"],
                  #print-scoring-sheet input[type="submit"] {
                    display: none !important;
                  }
                  #print-scoring-sheet input[type="text"],
                  #print-scoring-sheet input[type="number"] {
                    display: block !important;
                    border: none !important;
                    outline: none !important;
                    background: transparent !important;
                    padding: 0 !important;
                    text-align: center !important;
                    box-shadow: none !important;
                    font-weight: bold !important;
                    color: #000 !important;
                  }
                }
              `}</style>
              {/* Context Filter Desk */}
              <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                  <div>
                    <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Target Discipline</label>
                    <SearchableSelect
                      value={gameId}
                      onChange={(e) => {
                        setGameId(e.target.value);
                        setSelectedClassIds([]);
                        setSelectedClassDropdown("all");
                      }}
                      className="w-full border border-slate-200 rounded-xl p-2.5 focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">-- Choose Discipline --</option>
                      {games.map((g) => (
                        <option key={g.id} value={g.id}>{g.game_name}</option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Classification Dropdown</label>
                    <select
                      value={selectedClassDropdown}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedClassDropdown(val);
                        if (val === "all") {
                          setSelectedClassIds([]);
                        } else {
                          setSelectedClassIds([val]);
                        }
                      }}
                      className="w-full border border-slate-200 rounded-xl p-2.5 text-sm"
                    >
                      <option value="all">All Classifications</option>
                      {classifications.map((c) => (
                        <option key={c.id} value={c.id}>{c.classification}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Entry Mode</label>
                    <select
                      value={entryMode}
                      onChange={(e) => setEntryMode(e.target.value)}
                      className="w-full border border-slate-200 rounded-xl p-2.5 text-sm"
                    >
                      <option value="auto">Auto (from Game Type)</option>
                      <option value="triathlon">Triathlon Multi-Segment (Swim, T1, Cycle, T2, Run)</option>
                      <option value="time">Time Entry (mm:ss:ms)</option>
                      <option value="distance">Attempt Distance Entry (m)</option>
                      <option value="points">Points Entry</option>
                      <option value="bracket">Matches / Bracket Entry</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={saveResults}
                      disabled={busy || !players.length || (effectiveEntryMode === "bracket" && isTeamMode)}
                      className="flex-1 h-11 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 text-xs"
                    >
                      Save Scores
                    </button>
                    <button
                      onClick={computeRanks}
                      disabled={busy || !gameId}
                      className="flex-1 h-11 bg-violet-600 text-white font-bold rounded-xl hover:bg-violet-700 disabled:opacity-50 text-xs"
                    >
                      Compute ranks
                    </button>
                  </div>
                </div>

                {/* Class list checkboxes */}
                {classifications.length > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <div className="mb-4 flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <input
                          id="mix-classifications-results"
                          type="checkbox"
                          checked={mixClassifications}
                          onChange={(e) => setMixClassifications(e.target.checked)}
                          className="w-4 h-4 rounded text-indigo-600 border-slate-350 focus:ring-indigo-500"
                        />
                        <label htmlFor="mix-classifications-results" className="text-xs font-bold uppercase text-slate-650 cursor-pointer">
                          Mix selected classifications for compute
                        </label>
                      </div>
                      {isAthleticsEvent && (
                        <p className="text-xs text-indigo-600 font-semibold bg-indigo-50/50 border border-indigo-100 rounded-lg p-2">
                          💡 Athletics mode active: Track & field formulas will calculate medal points. Check the mix option if you want to rank selected classifications together.
                        </p>
                      )}
                    </div>
                    <span className="block text-xs font-bold uppercase text-slate-500 mb-2">Classification Filters</span>
                    <div className="flex flex-wrap gap-2">
                      {classifications.map((c) => {
                        const count = classCounts[c.id] || 0;
                        return (
                          <label key={c.id} className="flex items-center gap-1 text-xs bg-slate-50 border rounded-lg px-2.5 py-1">
                            <input
                              type="checkbox"
                              checked={selectedClassIds.includes(String(c.id))}
                              onChange={(e) => {
                                const cid = String(c.id);
                                setSelectedClassIds((prev) => {
                                  const next = e.target.checked ? [...prev, cid] : prev.filter((x) => x !== cid);
                                  setSelectedClassDropdown("all");
                                  return next;
                                });
                              }}
                            />
                            <span>{c.classification} ({count})</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>

              {/* Visibility settings panel */}
              {liveSettingsVisible && (
                <section className="bg-white border border-blue-100 rounded-3xl p-6 shadow-sm">
                  <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>
                      <h3 className="font-bold text-slate-800">Live Result Visibility Panel</h3>
                      <p className="text-xs text-slate-500">Toggle whether standings are shown publicly.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSaveLiveSettings(true)}
                        disabled={liveEnabled || liveSettingsSaving}
                        className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-xl disabled:opacity-50 hover:bg-emerald-700"
                      >
                        Publish Results
                      </button>
                      <button
                        onClick={() => handleSaveLiveSettings(false)}
                        disabled={!liveEnabled || liveSettingsSaving}
                        className="px-4 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl disabled:opacity-50 hover:bg-amber-700"
                      >
                        Pause Live visibility
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter status/pause message for public scoreboard..."
                      value={pauseMessage}
                      onChange={(e) => setPauseMessage(e.target.value)}
                      className="flex-1 border rounded-xl px-3 py-2 text-sm"
                    />
                    <button
                      onClick={() => handleSaveLiveSettings(liveEnabled)}
                      className="px-4 py-2 bg-slate-800 text-white text-xs font-bold rounded-xl hover:bg-slate-700"
                    >
                      Save Message
                    </button>
                  </div>
                </section>
              )}

              {/* Dynamic Entry Sheets */}
              {effectiveEntryMode === "triathlon" && (
                <div id="print-scoring-sheet" className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm overflow-hidden">
                  {renderPrintHeader()}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                    <div>
                      <h3 className="font-bold text-slate-800 text-base">🏊 🚴 🏃 Para Triathlon Multi-Segment Entry</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Enter Swim 350m, T1 gap, Cycle 9.5km, T2 gap, and Run 2km. Total Finish Time is calculated automatically.</p>
                    </div>
                    {renderExportButtons("timed")}
                  </div>
                  {players.length > 0 ? (
                    <div className="space-y-6">
                      {groupPlayersByEvent(players).map((group, gIdx) => (
                        <div key={gIdx} className="space-y-2">
                          <div className="bg-slate-800 text-white font-bold text-center py-2 px-4 rounded-xl text-xs uppercase tracking-wide">
                            Event {gIdx + 1} | Game: {group.gName} | Classification: {group.cName} | Age Category: {group.aCat} | Gender: {group.gender}
                          </div>
                          <div className="overflow-x-auto border rounded-2xl">
                            <table className="w-full text-left text-sm">
                              <thead className="bg-slate-50 text-slate-600 border-b font-bold text-xs">
                                <tr>
                                  <th className="px-3 py-3 text-center">Bib</th>
                                  <th className="px-3 py-3">Competitor Name</th>
                                  <th className="px-3 py-3 text-indigo-600">Class</th>
                                  <th className="px-3 py-3 bg-blue-50 text-blue-900">Swim (350m)</th>
                                  <th className="px-3 py-3 bg-amber-50 text-amber-900">T1 (Swim→Cycle)</th>
                                  <th className="px-3 py-3 bg-emerald-50 text-emerald-900">Cycle (9.5km)</th>
                                  <th className="px-3 py-3 bg-amber-50 text-amber-900">T2 (Cycle→Run)</th>
                                  <th className="px-3 py-3 bg-purple-50 text-purple-900">Run (2km)</th>
                                  <th className="px-3 py-3 bg-cyan-100 text-cyan-950 font-bold text-center">Total Finish Time</th>
                                  <th className="px-3 py-3 text-center">Rank</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {group.rows.map((r) => {
                                  const key = `${r.registration_id}_${r.game_id || 0}`;
                                  const entry = entryByRow[key] || {};
                                  const calcTotal = calculateTriathlonTotalTime(
                                    entry.tri_swim,
                                    entry.tri_t1,
                                    entry.tri_cycle,
                                    entry.tri_t2,
                                    entry.tri_run
                                  );
                                  return (
                                    <tr key={key}>
                                      <td className="px-3 py-3 font-mono font-bold text-slate-700 text-center">{r.bib_no || r.registration_id || "-"}</td>
                                      <td className="px-3 py-3 font-bold text-slate-850">{r.player_name || r.name || "-"}</td>
                                      <td className="px-3 py-3 text-indigo-600 font-semibold">{r.classification_name || "Open"}</td>
                                      <td className="px-3 py-3 bg-blue-50/30">
                                        <input
                                          type="text"
                                          placeholder="mm:ss:ms"
                                          value={entry.tri_swim || ""}
                                          onChange={(e) => {
                                            const swim = formatTimeMask(e.target.value);
                                            const total = calculateTriathlonTotalTime(swim, entry.tri_t1, entry.tri_cycle, entry.tri_t2, entry.tri_run);
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], tri_swim: swim, result_time: total },
                                            }));
                                          }}
                                          className="w-24 border rounded-lg px-2 py-1 text-center font-mono text-xs font-semibold"
                                        />
                                      </td>
                                      <td className="px-3 py-3 bg-amber-50/50">
                                        <input
                                          type="text"
                                          placeholder="mm:ss:ms"
                                          value={entry.tri_t1 || ""}
                                          onChange={(e) => {
                                            const t1 = formatTimeMask(e.target.value);
                                            const total = calculateTriathlonTotalTime(entry.tri_swim, t1, entry.tri_cycle, entry.tri_t2, entry.tri_run);
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], tri_t1: t1, result_time: total },
                                            }));
                                          }}
                                          className="w-24 border rounded-lg px-2 py-1 text-center font-mono text-xs"
                                        />
                                      </td>
                                      <td className="px-3 py-3 bg-emerald-50/30">
                                        <input
                                          type="text"
                                          placeholder="mm:ss:ms"
                                          value={entry.tri_cycle || ""}
                                          onChange={(e) => {
                                            const cycle = formatTimeMask(e.target.value);
                                            const total = calculateTriathlonTotalTime(entry.tri_swim, entry.tri_t1, cycle, entry.tri_t2, entry.tri_run);
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], tri_cycle: cycle, result_time: total },
                                            }));
                                          }}
                                          className="w-24 border rounded-lg px-2 py-1 text-center font-mono text-xs font-semibold"
                                        />
                                      </td>
                                      <td className="px-3 py-3 bg-amber-50/50">
                                        <input
                                          type="text"
                                          placeholder="mm:ss:ms"
                                          value={entry.tri_t2 || ""}
                                          onChange={(e) => {
                                            const t2 = formatTimeMask(e.target.value);
                                            const total = calculateTriathlonTotalTime(entry.tri_swim, entry.tri_t1, entry.tri_cycle, t2, entry.tri_run);
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], tri_t2: t2, result_time: total },
                                            }));
                                          }}
                                          className="w-24 border rounded-lg px-2 py-1 text-center font-mono text-xs"
                                        />
                                      </td>
                                      <td className="px-3 py-3 bg-purple-50/30">
                                        <input
                                          type="text"
                                          placeholder="mm:ss:ms"
                                          value={entry.tri_run || ""}
                                          onChange={(e) => {
                                            const run = formatTimeMask(e.target.value);
                                            const total = calculateTriathlonTotalTime(entry.tri_swim, entry.tri_t1, entry.tri_cycle, entry.tri_t2, run);
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], tri_run: run, result_time: total },
                                            }));
                                          }}
                                          className="w-24 border rounded-lg px-2 py-1 text-center font-mono text-xs font-semibold"
                                        />
                                      </td>
                                      <td className="px-3 py-3 font-bold text-cyan-950 bg-cyan-100/70 text-center whitespace-nowrap">
                                        {calcTotal || entry.result_time || "-"}
                                      </td>
                                      <td className="px-3 py-3 text-center">
                                        <input
                                          type="number"
                                          placeholder="Rank"
                                          value={entry.result_position || ""}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], result_position: val },
                                            }));
                                          }}
                                          className="w-16 border rounded-lg px-1.5 py-1 text-center font-bold text-xs"
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400 italic">No competitors loaded. Select game and category.</div>
                  )}
                </div>
              )}

              {/* Dynamic Entry Sheets */}
              {effectiveEntryMode === "time" && (
                <div id="print-scoring-sheet" className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm overflow-hidden">
                  {renderPrintHeader()}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                    <h3 className="font-bold text-slate-800">Timed Race Entries (mm:ss:ms)</h3>
                    {renderExportButtons("timed")}
                  </div>
                  {players.length > 0 ? (
                    <div className="space-y-6">
                      {groupPlayersByEvent(players).map((group, gIdx) => (
                        <div key={gIdx} className="space-y-2">
                          <div className="bg-slate-800 text-white font-bold text-center py-2 px-4 rounded-xl text-xs uppercase tracking-wide">
                            Event {gIdx + 1} | Game: {group.gName} | Classification: {group.cName} | Age Category: {group.aCat} | Gender: {group.gender}
                          </div>
                          <div className="overflow-x-auto border rounded-2xl">
                            <table className="w-full text-left text-sm">
                              <thead className="bg-slate-50 text-slate-600 border-b font-bold">
                                <tr>
                                  <th className="px-6 py-4">Bib</th>
                                  <th className="px-6 py-4">Competitor Name</th>
                                  <th className="px-6 py-4">Category</th>
                                  <th className="px-6 py-4">Race Time</th>
                                  <th className="px-6 py-4">Assigned Rank</th>
                                  <th className="px-6 py-4">Medal Points</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {group.rows.map((r) => {
                                  const key = `${r.registration_id}_${r.game_id || 0}`;
                                  const entry = entryByRow[key] || {};
                                  return (
                                    <tr key={key}>
                                      <td className="px-6 py-4 font-mono font-bold text-slate-700">{r.bib_no || r.registration_id || "-"}</td>
                                      <td className="px-6 py-4 font-bold text-slate-850">{r.player_name || r.name || "-"}</td>
                                      <td className="px-6 py-4 text-indigo-600 font-semibold">{r.classification_name || "Open"}</td>
                                      <td className="px-6 py-4">
                                        <input
                                          type="text"
                                          placeholder="mm:ss:ms"
                                          value={entry.result_time || ""}
                                          onChange={(e) => {
                                            const val = formatTimeMask(e.target.value);
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], result_time: val },
                                            }));
                                          }}
                                          className="w-32 border rounded-lg px-2 py-1.5 text-center font-mono"
                                        />
                                      </td>
                                      <td className="px-6 py-4">
                                        <input
                                          type="number"
                                          placeholder="Rank"
                                          value={entry.result_position || ""}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], result_position: val },
                                            }));
                                          }}
                                          className="w-20 border rounded-lg px-2 py-1.5 text-center font-bold"
                                        />
                                      </td>
                                      <td className="px-6 py-4 font-bold text-slate-600">{r.points_score || "-"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400 italic">No competitors loaded. Select game and category.</div>
                  )}
                </div>
              )}

              {effectiveEntryMode === "distance" && (
                <div id="print-scoring-sheet" className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm overflow-hidden">
                  {renderPrintHeader()}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                    <h3 className="font-bold text-slate-800">Field Event Attempt Entries (m)</h3>
                    {renderExportButtons("points")}
                  </div>
                  {players.length > 0 ? (
                    <div className="space-y-6">
                      {groupPlayersByEvent(players).map((group, gIdx) => (
                        <div key={gIdx} className="space-y-2">
                          <div className="bg-slate-800 text-white font-bold text-center py-2 px-4 rounded-xl text-xs uppercase tracking-wide">
                            Event {gIdx + 1} | Game: {group.gName} | Classification: {group.cName} | Age Category: {group.aCat} | Gender: {group.gender}
                          </div>
                          <div className="overflow-x-auto border rounded-2xl">
                            <table className="w-full text-left text-sm">
                              <thead className="bg-slate-50 text-slate-600 border-b font-bold">
                                <tr>
                                  <th className="px-4 py-3">Bib</th>
                                  <th className="px-4 py-3">Athlete</th>
                                  <th className="px-4 py-3">Gender</th>
                                  <th className="px-4 py-3">Class</th>
                                  <th className="px-4 py-3">Attempt 1</th>
                                  <th className="px-4 py-3">Attempt 2</th>
                                  <th className="px-4 py-3">Attempt 3</th>
                                  <th className="px-4 py-3">Best Score</th>
                                  <th className="px-4 py-3">Assigned Rank</th>
                                  <th className="px-4 py-3">Medal Points</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {group.rows.map((r) => {
                                  const key = `${r.registration_id}_${r.game_id || 0}`;
                                  const entry = entryByRow[key] || { attempts: ["", "", ""] };
                                  const bestVal = getBestAttempt(entry.attempts);
                                  return (
                                    <tr key={key}>
                                      <td className="px-4 py-3 font-mono font-bold text-slate-700">{r.bib_no || r.registration_id || "-"}</td>
                                      <td className="px-4 py-3 font-bold text-slate-800">{r.player_name || r.name || "-"}</td>
                                      <td className="px-4 py-3 capitalize text-xs text-slate-500">{r.gender || "-"}</td>
                                      <td className="px-4 py-3 text-xs font-semibold text-slate-500">{r.classification_name || "Open"}</td>
                                      {[0, 1, 2].map((idx) => (
                                        <td className="px-4 py-3" key={idx}>
                                          <input
                                            type="number"
                                            step="any"
                                            placeholder={`Attempt ${idx + 1}`}
                                            value={entry.attempts[idx] || ""}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              const nextAttempts = [...entry.attempts];
                                              nextAttempts[idx] = val;
                                              setEntryByRow((prev) => {
                                                const updated = {
                                                  ...prev,
                                                  [key]: { ...prev[key], attempts: nextAttempts },
                                                };
                                                return autoCalculateRanks(updated);
                                              });
                                            }}
                                            className="w-24 border rounded-lg px-2 py-1 text-center"
                                          />
                                        </td>
                                      ))}
                                      <td className="px-4 py-3 font-bold text-indigo-700">{bestVal || "-"}</td>
                                      <td className="px-4 py-3">
                                        <input
                                          type="number"
                                          placeholder="Rank"
                                          value={entry.result_position || ""}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], result_position: val },
                                            }));
                                          }}
                                          className="w-20 border rounded-lg px-2 py-1 text-center font-bold"
                                        />
                                      </td>
                                      <td className="px-4 py-3 font-bold text-slate-600">{r.points_score || "-"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400 italic">No competitors loaded. Select game and category.</div>
                  )}
                </div>
              )}

              {effectiveEntryMode === "points" && (
                <div id="print-scoring-sheet" className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm overflow-hidden">
                  {renderPrintHeader()}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                    <h3 className="font-bold text-slate-800">Discipline Generic Points Entries</h3>
                    {renderExportButtons("points")}
                  </div>
                  {players.length > 0 ? (
                    <div className="space-y-6">
                      {groupPlayersByEvent(players).map((group, gIdx) => (
                        <div key={gIdx} className="space-y-2">
                          <div className="bg-slate-800 text-white font-bold text-center py-2 px-4 rounded-xl text-xs uppercase tracking-wide">
                            Event {gIdx + 1} | Game: {group.gName} | Classification: {group.cName} | Age Category: {group.aCat} | Gender: {group.gender}
                          </div>
                          <div className="overflow-x-auto border rounded-2xl">
                            <table className="w-full text-left text-sm">
                              <thead className="bg-slate-50 text-slate-600 border-b font-bold">
                                <tr>
                                  <th className="px-6 py-4">Bib</th>
                                  <th className="px-6 py-4">Competitor Name</th>
                                  <th className="px-6 py-4">Category</th>
                                  <th className="px-6 py-4">Points Mark</th>
                                  <th className="px-6 py-4">Assigned Rank</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {group.rows.map((r) => {
                                  const key = `${r.registration_id}_${r.game_id || 0}`;
                                  const entry = entryByRow[key] || {};
                                  return (
                                    <tr key={key}>
                                      <td className="px-6 py-4 font-mono font-bold text-slate-700">{r.bib_no || r.registration_id || "-"}</td>
                                      <td className="px-6 py-4 font-bold text-slate-850">{r.player_name || r.name || "-"}</td>
                                      <td className="px-6 py-4 text-indigo-600 font-semibold">{r.classification_name || "Open"}</td>
                                      <td className="px-6 py-4">
                                        <input
                                          type="number"
                                          step="any"
                                          placeholder="Points"
                                          value={entry.points_score || ""}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], points_score: val },
                                            }));
                                          }}
                                          className="w-28 border rounded-lg px-2 py-1.5 text-center font-bold"
                                        />
                                      </td>
                                      <td className="px-6 py-4">
                                        <input
                                          type="number"
                                          placeholder="Rank"
                                          value={entry.result_position || ""}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setEntryByRow((prev) => ({
                                              ...prev,
                                              [key]: { ...prev[key], result_position: val },
                                            }));
                                          }}
                                          className="w-20 border rounded-lg px-2 py-1.5 text-center font-bold"
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400 italic">No competitors loaded. Select game and category.</div>
                  )}
                </div>
              )}

              {/* Bracket mode matchups */}
              {effectiveEntryMode === "bracket" && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Bracket creation form & match pad */}
                  <div className="lg:col-span-1 space-y-6">
                    <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                      <h3 className="font-bold text-slate-800 mb-3">Create Fixture Match</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Round Name</label>
                          <input
                            type="text"
                            value={roundName}
                            onChange={(e) => setRoundName(e.target.value)}
                            className="w-full border rounded-xl px-3 py-2 text-sm"
                            placeholder="e.g. Round 1, Semi Final"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase text-slate-500 mb-1">{isTeamMode ? "Team 1" : "Player 1"}</label>
                          <select
                            value={p1}
                            onChange={(e) => setP1(e.target.value)}
                            className="w-full border rounded-xl px-3 py-2 text-sm"
                          >
                            <option value="">-- Choose Competitor --</option>
                            {(isTeamMode ? teams : players).map((x) => (
                              <option key={isTeamMode ? x.id : x.registration_id} value={isTeamMode ? x.id : x.registration_id}>
                                {isTeamMode ? x.team_name : `${x.name} - ${x.classification_name}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase text-slate-500 mb-1">{isTeamMode ? "Team 2" : "Player 2"}</label>
                          <select
                            value={p2}
                            onChange={(e) => setP2(e.target.value)}
                            className="w-full border rounded-xl px-3 py-2 text-sm"
                          >
                            <option value="">-- Choose Competitor --</option>
                            {(isTeamMode ? teams : players).map((x) => (
                              <option key={isTeamMode ? x.id : x.registration_id} value={isTeamMode ? x.id : x.registration_id}>
                                {isTeamMode ? x.team_name : `${x.name} - ${x.classification_name}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          onClick={handleCreateMatch}
                          className="w-full py-2 bg-indigo-600 text-white font-bold text-xs rounded-xl hover:bg-indigo-700 mt-2"
                        >
                          Create Match
                        </button>
                      </div>
                    </section>

                    {/* Incremental live match pad */}
                    {liveMatchId && (
                      <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm space-y-4">
                        <div className="flex justify-between items-center border-b pb-2">
                          <h3 className="font-bold text-slate-800">Live Match Control</h3>
                          <div className="flex gap-1.5">
                            {[1, 2, 3].map((step) => (
                              <button
                                key={step}
                                onClick={() => setLiveStep(step)}
                                className={`px-2 py-0.5 border rounded text-xs font-semibold ${liveStep === step ? "bg-indigo-600 text-white border-indigo-600" : "bg-white hover:bg-slate-50"}`}
                              >
                                {step} pt
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Incremental scores display */}
                        <div className="rounded-2xl bg-slate-900 text-emerald-300 p-5 font-mono text-5xl text-center shadow-inner tracking-wider">
                          {liveScore.left} - {liveScore.right}
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-center">
                          <div>
                            <span className="block text-xs font-bold text-slate-600 truncate mb-1" title={liveLeftLabel}>{liveLeftLabel}</span>
                            <div className="flex gap-1">
                              <button onClick={() => handleBumpLiveScore("left", -liveStep)} className="flex-1 py-2 bg-rose-100 hover:bg-rose-200 text-rose-700 font-extrabold text-sm rounded-lg">-</button>
                              <button onClick={() => handleBumpLiveScore("left", liveStep)} className="flex-1 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-extrabold text-sm rounded-lg">+</button>
                            </div>
                            <button
                              onClick={() => handleSetWinner("left")}
                              className={`w-full mt-2 py-1 text-xs border rounded-lg font-bold ${winnerByMatch[liveMatchId] === liveLeftId ? "bg-emerald-600 text-white" : "bg-white"}`}
                            >
                              Winner
                            </button>
                          </div>
                          <div>
                            <span className="block text-xs font-bold text-slate-600 truncate mb-1" title={liveRightLabel}>{liveRightLabel}</span>
                            <div className="flex gap-1">
                              <button onClick={() => handleBumpLiveScore("right", -liveStep)} className="flex-1 py-2 bg-rose-100 hover:bg-rose-200 text-rose-700 font-extrabold text-sm rounded-lg">-</button>
                              <button onClick={() => handleBumpLiveScore("right", liveStep)} className="flex-1 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-extrabold text-sm rounded-lg">+</button>
                            </div>
                            <button
                              onClick={() => handleSetWinner("right")}
                              className={`w-full mt-2 py-1 text-xs border rounded-lg font-bold ${winnerByMatch[liveMatchId] === liveRightId ? "bg-emerald-600 text-white" : "bg-white"}`}
                            >
                              Winner
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 pt-2 border-t text-xs">
                          <button onClick={handleSyncLiveScore} className="py-2 bg-slate-100 text-slate-700 font-bold rounded-lg hover:bg-slate-200">Sync Form</button>
                          <button onClick={() => setLiveScore({ left: 0, right: 0 })} className="py-2 bg-slate-100 text-slate-700 font-bold rounded-lg hover:bg-slate-200">Reset</button>
                          <button onClick={() => handleSaveLiveResult(false)} className="py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700">Save</button>
                          <button onClick={() => handleSaveLiveResult(true)} className="py-2 bg-emerald-600 text-white font-bold rounded-lg hover:bg-emerald-700">Save & Next</button>
                        </div>
                      </section>
                    )}
                  </div>

                  {/* Matches List Grid */}
                  <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200 p-6 shadow-sm flex flex-col">
                    <h3 className="font-bold text-slate-800 mb-4">Bracket Match Fixtures</h3>
                    {matches.length > 0 ? (
                      <div className="overflow-x-auto flex-1 border rounded-2xl">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
                            <tr>
                              <th className="px-4 py-3">Round</th>
                              <th className="px-4 py-3">Competitor 1</th>
                              <th className="px-4 py-3">Competitor 2</th>
                              <th className="px-4 py-3">Sets Score</th>
                              <th className="px-4 py-3">Declared Winner</th>
                              <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {matches.map((m) => {
                              const leftId = String(isTeamMode ? m.team1_id || "" : m.player1_reg_id || "");
                              const rightId = String(isTeamMode ? m.team2_id || "" : m.player2_reg_id || "");
                              const currentScore = scoreByMatch[m.id] !== undefined ? scoreByMatch[m.id] : m.score || "";
                              const currentWinner = winnerByMatch[m.id] !== undefined ? winnerByMatch[m.id] : m.winner_id || "";
                              return (
                                <tr key={m.id} className={String(m.id) === String(liveMatchId) ? "bg-indigo-50/40" : ""}>
                                  <td className="px-4 py-3 font-semibold text-slate-500 text-xs">{m.round_name || "Round 1"}</td>
                                  <td className="px-4 py-3 font-bold text-slate-800 truncate max-w-[120px]" title={isTeamMode ? m.team1_name : m.player1_name}>
                                    {isTeamMode ? m.team1_name : m.player1_name || "BYE"}
                                  </td>
                                  <td className="px-4 py-3 font-bold text-slate-800 truncate max-w-[120px]" title={isTeamMode ? m.team2_name : m.player2_name}>
                                    {isTeamMode ? m.team2_name : m.player2_name || "BYE"}
                                  </td>
                                  <td className="px-4 py-3">
                                    <input
                                      type="text"
                                      placeholder="0-0"
                                      value={currentScore}
                                      onChange={(e) => setScoreByMatch({ ...scoreByMatch, [m.id]: e.target.value })}
                                      className="w-16 border rounded px-1.5 py-1 text-center font-mono text-xs"
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <select
                                      value={currentWinner}
                                      onChange={(e) => setWinnerByMatch({ ...winnerByMatch, [m.id]: e.target.value })}
                                      className="border rounded px-1.5 py-1 text-xs"
                                    >
                                      <option value="">Choose Winner</option>
                                      {leftId && <option value={leftId}>{isTeamMode ? m.team1_name : m.player1_name}</option>}
                                      {rightId && <option value={rightId}>{isTeamMode ? m.team2_name : m.player2_name}</option>}
                                    </select>
                                  </td>
                                  <td className="px-4 py-3 text-right flex gap-1.5 justify-end">
                                    <button
                                      onClick={() => setLiveMatchId(String(m.id))}
                                      className="px-2.5 py-1 text-xs border rounded bg-white hover:bg-slate-50 text-slate-700 font-bold"
                                    >
                                      Live
                                    </button>
                                    <button
                                      onClick={() => handleSaveMatchResult(m, currentWinner, currentScore)}
                                      className="px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded font-bold"
                                    >
                                      Save
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center py-12 text-slate-400 border border-dashed rounded-2xl">
                        <span className="text-3xl mb-1">🎮</span>
                        <p className="text-sm italic">Create your first match above or generate brackets in Game Engine.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Team Manager Builder */}
              {effectiveEntryMode === "bracket" && isTeamMode && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 border-t pt-6">
                  {/* Create team & list teams */}
                  <div className="lg:col-span-1 bg-white rounded-3xl border border-slate-200 p-6 shadow-sm space-y-4">
                    <h3 className="font-bold text-slate-800">Team Generator</h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Team Name..."
                        value={newTeamName}
                        onChange={(e) => setNewTeamName(e.target.value)}
                        className="flex-1 border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        onClick={handleCreateTeam}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl"
                      >
                        Create
                      </button>
                    </div>

                    <div className="border rounded-2xl p-2 max-h-72 overflow-y-auto bg-slate-50/50">
                      <span className="block text-xs font-bold uppercase text-slate-500 p-2 border-b">Existing Teams</span>
                      {teams.length > 0 ? (
                        <ul className="divide-y divide-slate-100">
                          {teams.map((t) => (
                            <li
                              key={t.id}
                              onClick={() => setSelectedTeam(String(t.id))}
                              className={`p-3 text-xs font-bold text-slate-700 hover:bg-white rounded-lg cursor-pointer flex justify-between items-center transition-colors ${selectedTeam === String(t.id) ? "bg-white border shadow-sm border-slate-200" : ""}`}
                            >
                              <span>{t.team_name}</span>
                              <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-[10px]">
                                {t.member_count || 0} members
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="p-3 text-slate-400 italic text-xs text-center">No teams created.</p>
                      )}
                    </div>
                  </div>

                  {/* Add Members to team */}
                  <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-800 mb-1">Assemble Members</h3>
                    <p className="text-xs text-slate-500 mb-4">Select a team from the left, then click Add to Team for unallocated players.</p>

                    {players.length > 0 ? (
                      <div className="overflow-x-auto border rounded-2xl max-h-96">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-slate-50 text-slate-600 font-bold border-b">
                            <tr>
                              <th className="px-4 py-3">Athlete</th>
                              <th className="px-4 py-3">Category</th>
                              <th className="px-4 py-3 text-right">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {players
                              .filter((p) => {
                                // Exclude players already in some team
                                return !teams.some((team) =>
                                  (Array.isArray(team.members) ? team.members : []).some((m) => Number(m.player_id) === Number(p.player_id))
                                );
                              })
                              .map((p) => (
                                <tr key={p.registration_id}>
                                  <td className="px-4 py-3 font-bold text-slate-800">{p.name}</td>
                                  <td className="px-4 py-3 text-xs text-slate-500 font-semibold">{p.classification_name || "Open"}</td>
                                  <td className="px-4 py-3 text-right">
                                    <button
                                      onClick={() => handleAddTeamMember(selectedTeam || (teams[0] ? String(teams[0].id) : ""), p.registration_id)}
                                      disabled={!selectedTeam && !teams.length}
                                      className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold text-xs disabled:bg-slate-200 disabled:text-slate-400"
                                    >
                                      Add to Team
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-slate-400 italic text-sm text-center py-6">No players available to assign.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Medals & Leaderboard */}
          {activeTab === "leaderboard" && (
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-6 animate-fadeIn">
              <div className="flex justify-between items-center flex-wrap gap-4 border-b border-slate-100 pb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Final Results & Medals Dashboard</h2>
                  <p className="text-xs text-slate-500">Official scoreboard ranking for current event disciplines.</p>
                </div>
                {renderLeaderboardExportButtons()}
              </div>

              {/* Selection Status */}
              <div className="flex justify-between items-center flex-wrap gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-sm">
                  <span className="text-xs font-extrabold text-slate-500 uppercase">Selection Status:</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border ${getSelectedStatus().badge}`}>
                    {getSelectedStatus().text}
                  </span>
                </div>
              </div>

              {/* Games / Disciplines Selector Grid */}
              {games.length > 0 && (
                <div className="space-y-2 border-b border-slate-100 pb-4">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Select Discipline Table:</label>
                  <div className="flex flex-wrap gap-2.5">
                    {games.map((g) => {
                      const isActive = String(g.id) === String(gameId);
                      const badgeColor =
                        g.status === "Completed" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                          g.status === "Partial" ? "bg-blue-100 text-blue-800 border-blue-200" :
                            "bg-amber-100 text-amber-800 border-amber-200";

                      const dotColor =
                        g.status === "Completed" ? "bg-emerald-500" :
                          g.status === "Partial" ? "bg-blue-500" :
                            "bg-amber-500";

                      return (
                        <button
                          key={g.id}
                          onClick={() => {
                            setGameId(String(g.id));
                            setSelectedClassDropdown("all");
                            setSelectedClassIds([]);
                          }}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border text-xs font-bold transition-all ${isActive
                            ? "bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-100 scale-[1.02]"
                            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                            }`}
                        >
                          <span>{g.game_name}</span>
                          <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border ${isActive ? "bg-indigo-700 text-white border-indigo-500" : badgeColor
                            }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-white" : dotColor}`} />
                            {g.status}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Classification Selector Grid */}
              {classifications.length > 0 && (
                <div className="space-y-2 border-b border-slate-100 pb-4">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Select Classification:</label>
                  <div className="flex flex-wrap gap-2.5">
                    <button
                      onClick={() => {
                        setSelectedClassDropdown("all");
                        setSelectedClassIds([]);
                      }}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border text-xs font-bold transition-all ${selectedClassDropdown === "all"
                        ? "bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-100 scale-[1.02]"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                        }`}
                    >
                      All Classifications
                    </button>
                    {classifications.map((c) => {
                      const isActive = selectedClassDropdown === String(c.id);
                      const count = classCounts[c.id] || 0;
                      const status = getClassificationStatus(c.id);
                      return (
                        <button
                          key={c.id}
                          onClick={() => {
                            setSelectedClassDropdown(String(c.id));
                            setSelectedClassIds([String(c.id)]);
                          }}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border text-xs font-bold transition-all ${isActive
                            ? "bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-100 scale-[1.02]"
                            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                            }`}
                        >
                          <span>{c.classification}</span>
                          <span
                            className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border ${isActive ? "bg-indigo-700 text-white border-indigo-500" : "bg-slate-100 text-slate-600 border-slate-200"
                              }`}
                          >
                            {count}
                          </span>
                          <span
                            className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border ${isActive ? "bg-indigo-700 text-white border-indigo-500" : status.badge
                              }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-white" : status.dot}`} />
                            {status.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {players.length > 0 ? (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-bold">
                      <tr>
                        <th className="px-6 py-4">Rank</th>
                        <th className="px-6 py-4">Athlete Name</th>
                        <th className="px-6 py-4">Bib No</th>
                        <th className="px-6 py-4">Gender</th>
                        <th className="px-6 py-4">District</th>
                        <th className="px-6 py-4">Classification</th>
                        <th className="px-6 py-4">Discipline</th>
                        <th className="px-6 py-4">Best Performance</th>
                        {isAthleticsEvent && <th className="px-6 py-4">Medal Points</th>}
                        <th className="px-6 py-4">Medal Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {players
                        .slice()
                        .sort((a, b) => {
                          const gameA = a.game_name || "";
                          const gameB = b.game_name || "";
                          const cmpGame = gameA.localeCompare(gameB);
                          if (cmpGame !== 0) return cmpGame;

                          const genA = a.gender || "";
                          const genB = b.gender || "";
                          const cmpGen = genA.localeCompare(genB);
                          if (cmpGen !== 0) return cmpGen;

                          const classA = a.classification_name || "";
                          const classB = b.classification_name || "";
                          const cmpClass = classA.localeCompare(classB);
                          if (cmpClass !== 0) return cmpClass;

                          const rankA = isNaN(parseInt(a.result_position)) ? 999999 : parseInt(a.result_position);
                          const rankB = isNaN(parseInt(b.result_position)) ? 999999 : parseInt(b.result_position);
                          return rankA - rankB;
                        })
                        .map((r, idx) => {
                          const medal =
                            r.result_position === 1 ? "🥇 Gold" :
                              r.result_position === 2 ? "🥈 Silver" :
                                r.result_position === 3 ? "🥉 Bronze" : "Competitor";
                          return (
                            <tr key={idx} className="hover:bg-slate-50/50">
                              <td className="px-6 py-4 font-bold text-slate-900 text-lg">#{r.result_position || "-"}</td>
                              <td className="px-6 py-4 font-bold text-slate-800">{r.player_name || r.name || "-"}</td>
                              <td className="px-6 py-4 font-mono font-bold text-slate-700">{r.player_registration_id || r.registration_id || "-"}</td>
                              <td className="px-6 py-4 text-slate-600 capitalize">{r.gender || "-"}</td>
                              <td className="px-6 py-4 text-slate-600">{r.district || "-"}</td>
                              <td className="px-6 py-4 font-semibold text-indigo-600">{r.classification_name || "Open"}</td>
                              <td className="px-6 py-4 text-indigo-700 font-semibold">{r.game_name || "-"}</td>
                              <td className="px-6 py-4 font-mono font-bold text-slate-800">
                                {effectiveEntryMode === "distance"
                                  ? (getBestAttempt(r.attempts) ? `${getBestAttempt(r.attempts)} m` : "-")
                                  : (r.result_time || r.points_score || "-")}
                              </td>
                              {isAthleticsEvent && (
                                <td className="px-6 py-4 font-bold text-indigo-650">
                                  {r.points_score || "-"}
                                </td>
                              )}
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${r.result_position === 1 ? "bg-amber-100 text-amber-800" :
                                  r.result_position === 2 ? "bg-slate-100 text-slate-800" :
                                    r.result_position === 3 ? "bg-orange-100 text-orange-800" : "bg-slate-100 text-slate-600"
                                  }`}>
                                  {medal}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400 italic">No score results computed. Select a different discipline or gender combination above.</div>
              )}
            </div>
          )}

          {/* Tab 5: Certificates Desk */}
          {activeTab === "certificates" && (
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-6 animate-fadeIn">
              <div className="flex justify-between items-center flex-wrap gap-4 border-b pb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Certificate Generation Suite</h2>
                  <p className="text-xs text-slate-500">Produce merit and participation PDF certificates dynamically.</p>
                </div>
                <button
                  onClick={handleBulkDownload}
                  disabled={!eventId}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold text-sm shadow-md hover:from-emerald-700 disabled:opacity-50"
                >
                  Bulk Download Certificates (ZIP)
                </button>
              </div>

              {(() => {
                const rankGeneratedPlayers = players.filter(
                  (r) => r.result_position !== null && r.result_position !== undefined && r.result_position !== ""
                );
                return rankGeneratedPlayers.length > 0 ? (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-bold">
                      <tr>
                        <th className="px-6 py-4">Competitor</th>
                        <th className="px-6 py-4">Discipline</th>
                        <th className="px-6 py-4">Classification</th>
                        <th className="px-6 py-4">Rank achieved</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rankGeneratedPlayers.map((r, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50">
                          <td className="px-6 py-4 font-bold text-slate-800">{r.player_name || r.name || "-"}</td>
                          <td className="px-6 py-4 text-slate-600">{r.game_name}</td>
                          <td className="px-6 py-4 font-semibold text-indigo-600">{r.classification_name || "Open"}</td>
                          <td className="px-6 py-4 font-bold">Rank {r.result_position || "-"}</td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleDownloadCertificate(r.registration_id, true)}
                              className="px-3.5 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-bold text-xs shadow-sm"
                            >
                              Generate Certificate
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                ) : (
                <div className="text-center py-12 text-slate-400 italic">No competitors with a generated rank yet for this discipline.</div>
                );
              })()}


              {/* Revocation log */}
              <div className="border-t pt-6 space-y-4">
                <div>
                  <h3 className="text-md font-bold text-slate-800">Certificate Revocation Log</h3>
                  <p className="text-xs text-slate-500">Track, revoke, and reissue generated certificates.</p>
                </div>
                {issuedCertificates.length > 0 ? (
                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-bold">
                        <tr>
                          <th className="px-6 py-3">Competitor</th>
                          <th className="px-6 py-3">Certificate No.</th>
                          <th className="px-6 py-3">Certificate Type</th>
                          <th className="px-6 py-3">Generated At</th>
                          <th className="px-6 py-3">Status</th>
                          <th className="px-6 py-3 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {issuedCertificates.map((cert) => {
                          const rawMedalType = cert.medal_type || cert.type || "";
                          const isMerit = ["Gold", "Silver", "Bronze", "Merit"].includes(rawMedalType)
                            || (!rawMedalType && !!cert.registration_id);
                          const certType = isMerit ? "Merit" : "Participation";
                          const generatedAt = cert.issued_at || cert.created_at;
                          const generatedLabel = generatedAt && !Number.isNaN(new Date(generatedAt).getTime())
                            ? new Date(generatedAt).toLocaleString()
                            : "-";
                          const isRevoked = toInt(cert.revoked) === 1;
                          return (
                            <tr key={cert.id} className="hover:bg-slate-50/50">
                              <td className="px-6 py-3 font-bold text-slate-800">{cert.player_name}</td>
                              <td className="px-6 py-3 font-mono text-xs text-slate-600">{cert.serial_no || "-"}</td>
                              <td className="px-6 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isMerit ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700"}`}>
                                  {certType}
                                </span>
                              </td>
                              <td className="px-6 py-3 text-slate-500 font-mono text-xs">{generatedLabel}</td>
                              <td className="px-6 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isRevoked ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                                  {isRevoked ? "Revoked" : "Active"}
                                </span>
                              </td>
                              <td className="px-6 py-3 text-right">
                                <div className="flex justify-end items-center gap-1.5">
                                  {isRevoked ? (
                                    <button
                                      onClick={() => handleReissueCertificate(cert.id)}
                                      className="px-2.5 py-1 text-xs border rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold inline-flex items-center gap-1"
                                    >
                                      <FaRedo size={10} /> Reissue
                                    </button>
                                  ) : (
                                    <>
                                      {cert.registration_id && (
                                        <>
                                          <button
                                            onClick={() => handleDownloadCertificate(cert.registration_id, false)}
                                            className="px-2.5 py-1 text-xs border rounded bg-slate-50 text-slate-700 hover:bg-slate-100 font-bold"
                                          >
                                            Download
                                          </button>
                                          <button
                                            onClick={() => handleDownloadCertificate(cert.registration_id, true)}
                                            className="px-2.5 py-1 text-xs border rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-bold"
                                          >
                                            Reprint
                                          </button>
                                        </>
                                      )}
                                      <button
                                        onClick={() => handleRevokeCertificate(cert.id)}
                                        className="px-2.5 py-1 text-xs border rounded bg-rose-50 text-rose-700 hover:bg-rose-100 font-bold"
                                      >
                                        Revoke
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center py-6 text-slate-400 italic text-sm">No issued certificates found.</p>
                )}
              </div>
            </div>
          )}

          {/* Tab 6: District Medals */}
          {activeTab === "districtMedals" && (
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4 animate-fadeIn">
              <div className="flex justify-between items-center flex-wrap gap-3 border-b pb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">District Medal & Participation Summary</h2>
                  <p className="text-xs text-slate-500">District-wise Gold, Silver, Bronze and participation counts for this event.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleDistrictMedalsPdf}
                    disabled={!districtMedals.length}
                    className="px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-bold text-xs disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <FaFilePdf /> PDF
                  </button>
                  <button
                    onClick={handleDistrictMedalsPrint}
                    disabled={!districtMedals.length}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 font-bold text-xs text-slate-700 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <FaPrint /> Print
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ["Gold", districtMedalTotals.gold, "text-amber-700 bg-amber-50 border-amber-100"],
                  ["Silver", districtMedalTotals.silver, "text-slate-700 bg-slate-50 border-slate-200"],
                  ["Bronze", districtMedalTotals.bronze, "text-orange-700 bg-orange-50 border-orange-100"],
                  ["Participation", districtMedalTotals.participation, "text-indigo-700 bg-indigo-50 border-indigo-100"],
                ].map(([label, value, tone]) => (
                  <div key={label} className={`rounded-2xl border p-4 ${tone}`}>
                    <p className="text-[10px] uppercase font-extrabold tracking-wide opacity-75">{label}</p>
                    <p className="text-2xl font-black mt-1">{value}</p>
                  </div>
                ))}
              </div>

              {districtMedals.length > 0 ? (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gradient-to-r from-indigo-700 to-blue-700 text-white font-bold">
                      <tr>
                        <th className="px-6 py-3">District</th>
                        <th className="px-6 py-3">Gold</th>
                        <th className="px-6 py-3">Silver</th>
                        <th className="px-6 py-3">Bronze</th>
                        <th className="px-6 py-3">Participation</th>
                        <th className="px-6 py-3">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {districtMedals.map((row, idx) => (
                        <tr key={`${row.district || "district"}-${idx}`} className="hover:bg-slate-50/70">
                          <td className="px-6 py-3 font-bold text-slate-800">{row.district || "Unknown"}</td>
                          <td className="px-6 py-3 font-bold text-amber-700">{toInt(row.gold)}</td>
                          <td className="px-6 py-3 font-bold text-slate-600">{toInt(row.silver)}</td>
                          <td className="px-6 py-3 font-bold text-orange-700">{toInt(row.bronze)}</td>
                          <td className="px-6 py-3 font-bold text-indigo-700">{toInt(row.participation)}</td>
                          <td className="px-6 py-3 font-black text-slate-900">{toInt(row.gold) + toInt(row.silver) + toInt(row.bronze) + toInt(row.participation)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-6 text-slate-400 italic text-sm">No district medal summary found for this event.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PDF Print Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl h-[90vh] flex flex-col p-4">
            <div className="flex items-center justify-between pb-3 border-b mb-3">
              <span className="font-bold text-slate-800">Print Preview</span>
              <div className="flex gap-2">
                <button
                  onClick={() => window.print()}
                  className="px-3 py-1.5 rounded-lg border text-sm font-semibold hover:bg-slate-50"
                >
                  Print
                </button>
                <button
                  onClick={() => {
                    window.URL.revokeObjectURL(previewUrl);
                    setPreviewUrl("");
                  }}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-sm font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <PDFViewer file={previewUrl} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
